import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';

type Summary = {
  school: { id: number; name: string; county: string; city: string };
  counts: { teachers: number; students: number; parents: number; admins: number; classes: number };
  rating?: {
    rating_percent?: number | null;
    avg_grade?: number | null;
    grades_count?: number;
    absences_total?: number;
    absences_unjustified?: number;
    absences_unjustified_7d?: number;
  };
  alerts?: Array<{
    key: string;
    severity: 'high' | 'warning' | 'info';
    title: string;
    message: string;
    link?: string | null;
  }>;
};

type UserRow = {
  id: number;
  full_name: string;
  email: string;
  role: string;
  classroom_id?: number | null;
  classroom_name?: string | null;
  is_suspended: boolean;
  is_graduated?: boolean;
  graduated_at?: string | null;
  graduated_school_year?: string | null;
};

type SecretariatUserRow = {
  id: number;
  full_name: string;
  email: string;
  created_at?: string | null;
};

type UserDetails = {
  id: number;
  full_name: string;
  email: string;
  role: string;
  phone?: string | null;
  titles?: string[] | string | null;
  is_suspended: boolean;
  is_deleted?: boolean;
  is_graduated?: boolean;
  graduated_at?: string | null;
  graduated_school_year?: string | null;
  classroom?: { id: number; name: string } | null;
  subjects?: Array<{ id: number; name: string; classroom_id: number | null; classroom_name: string | null }>;
  children?: Array<{ id: number; full_name: string; email: string; classroom_id: number | null; classroom_name: string | null }>;
  parents?: Array<{ id: number | null; full_name: string | null; email: string | null; phone: string | null }>;
  catalog?: {
    subjects: Array<{ subject_id: number | null; subject_name: string | null; avg_grade: number | null; grades_count: number }>;
    absences_total: number;
    absences_unjustified: number;
  };
  catalog_full?: {
    limit: number;
    grades: Array<{
      id: number;
      subject_id: number | null;
      subject_name: string | null;
      value: number | null;
      qualifier: string | null;
      date: string | null;
      created_at: string | null;
      comment: string | null;
      is_conduct: boolean;
      is_thesis: boolean;
      is_final: boolean;
      is_homework: boolean;
      teacher_id: number | null;
      teacher_name: string | null;
    }>;
    absences: Array<{
      id: number;
      subject_id: number | null;
      subject_name: string | null;
      date: string | null;
      created_at: string | null;
      justified: boolean;
      justified_raw: string | null;
    }>;
  };
};

type SchoolClasses = {
  school_classes: Array<{
    id: number;
    name: string;
    rating_percent?: number | null;
    avg_grade?: number | null;
    grades_count?: number;
    students_count?: number;
    absences_total?: number;
    absences_unjustified?: number;
  }>;
  classrooms: Array<{
    id: number;
    name: string;
    admin_id: number | null;
    admins?: Array<{ id: number; full_name: string; email: string; role: string }>;
  }>;
};

type TabKey = 'overview' | 'users' | 'classes' | 'reports' | 'audit' | 'settings';
type ReportsResponse = {
  days: number;
  items: Array<{
    id: number;
    full_name: string;
    email: string;
    classroom_id: number | null;
    classroom_name: string | null;
    avg_grade: number | null;
    grades_count: number;
    absences_total: number;
    absences_unjustified: number;
    rating_percent: number | null;
  }>;
};

type ClassRatingsResponse = {
  items: Array<{
    id: number;
    name: string;
    rating_percent: number | null;
    avg_grade: number | null;
    grades_count: number;
    students_count: number;
    absences_total: number;
    absences_unjustified: number;
  }>;
};

type AbsencesByClassResponse = {
  days: number;
  items: Array<{
    id: number;
    name: string;
    students_count: number;
    absences_total: number;
    absences_unjustified: number;
  }>;
};

type AttendanceTodayResponse = {
  date: string;
  students_total: number;
  absences_total: number;
  absences_unjustified: number;
  absent_students: number;
  absent_students_unjustified: number;
  by_class: Array<{
    id: number;
    name: string;
    absences_total: number;
    absences_unjustified: number;
    absent_students: number;
  }>;
};

type TeacherKpisResponse = {
  days: number;
  items: Array<{
    id: number;
    full_name: string;
    email: string;
    subjects_count: number;
    classes_taught: number;
    grades_count: number;
    students_touched: number;
    avg_grade: number | null;
    absences_total: number;
    absences_unjustified: number;
  }>;
};

type SettingsResponse = {
  school_id: number;
  absence_penalty_per_student: number;
  absence_penalty_cap: number;
  unjustified_absences_7d_threshold: number;
  auto_notifications_enabled: boolean;
  auto_notifications_frequency: 'daily' | 'weekly' | string;
  auto_notifications_last_sent_at: string | null;
  current_school_year?: string | null;
  current_structure?: 'semestru' | 'trimestru' | 'module' | string | null;
  current_education_level?: 'primar' | 'gimnazial' | 'liceal' | 'universitar' | string | null;
  updated_at: string | null;
};

type TeacherRow = {
  id: number;
  full_name: string;
  email: string;
  phone: string | null;
  created_at: string | null;
  is_suspended: boolean;
};

type SubjectRow = {
  id: number;
  name: string;
  classroom_id: number | null;
  professor_id: number;
  professor_name: string;
  professor_email: string;
};

type AuditGradeEvent = {
  id: number;
  created_at: string;
  actor_user_id?: number | null;
  actor_name?: string | null;
  actor_role?: string | null;
  actor_ip?: string | null;
  action: string;
  grade_id: number;
  old_data?: any | null;
  new_data?: any | null;
};

type AuditEvent = {
  id: number;
  created_at: string;
  action: string;
  entity_type: string;
  entity_id: number;
  subject_user_id?: number | null;
  subject_name?: string | null;
  classroom_id?: number | null;
  actor_user_id?: number | null;
  actor_name?: string | null;
  actor_role?: string | null;
  actor_ip?: string | null;
  old_data?: any | null;
  new_data?: any | null;
};

export default function SchoolAdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { me, uiRole, signOut } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const user = useMemo(() => {
    return {
      role: (uiRole || 'admin') as 'admin' | 'teacher' | 'student' | 'parent',
      name: me?.full_name || 'Administrator',
      avatar: me?.avatar_url || undefined,
    };
  }, [me?.avatar_url, me?.full_name, uiRole]);

  const showSidebar = user.role === 'admin';
  const isReadOnly = false;

  const [tab, setTab] = useState<TabKey>('overview');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [classes, setClasses] = useState<SchoolClasses | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classGraduateLoading, setClassGraduateLoading] = useState<Record<number, boolean>>({});
  const [classesError, setClassesError] = useState<string | null>(null);
  const loading = summaryLoading || usersLoading || classesLoading;
  const [publishingAlerts, setPublishingAlerts] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState<UserDetails | null>(null);
  const [userEditMode, setUserEditMode] = useState(false);
  const [userEditSaving, setUserEditSaving] = useState(false);
  const [userEditForm, setUserEditForm] = useState({ full_name: '', email: '', phone: '', address: '' });
  const [userExportLoading, setUserExportLoading] = useState(false);
  const [userDeleteLoading, setUserDeleteLoading] = useState(false);
  const [userGraduateLoading, setUserGraduateLoading] = useState(false);
  const [teachers, setTeachers] = useState<TeacherRow[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [createTeacherOpen, setCreateTeacherOpen] = useState(false);
  const [createTeacherLoading, setCreateTeacherLoading] = useState(false);
  const [createTeacherForm, setCreateTeacherForm] = useState({ full_name: '', email: '', phone: '' });

  const [createClassOpen, setCreateClassOpen] = useState(false);
  const [createClassLoading, setCreateClassLoading] = useState(false);
  const [createClassName, setCreateClassName] = useState('');
  const [createClassAdminTeacherId, setCreateClassAdminTeacherId] = useState<string>('');

  const [subjectsOpen, setSubjectsOpen] = useState(false);
  const [subjectsClassroomId, setSubjectsClassroomId] = useState<number | null>(null);
  const [subjectsClassroomName, setSubjectsClassroomName] = useState<string>('');
  const [subjects, setSubjects] = useState<SubjectRow[]>([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState('');
  const [newSubjectName, setNewSubjectName] = useState('');
  const [newSubjectTeacherId, setNewSubjectTeacherId] = useState<string>('');
  const [newSubjectLoading, setNewSubjectLoading] = useState(false);
  const [studentGradeAudit, setStudentGradeAudit] = useState<AuditGradeEvent[]>([]);
  const [studentGradeAuditLoading, setStudentGradeAuditLoading] = useState(false);
  const [studentGradeAuditError, setStudentGradeAuditError] = useState('');

  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditEntityType, setAuditEntityType] = useState<string>(''); // '' = all
  const [auditAction, setAuditAction] = useState<string>('');
  const [auditFrom, setAuditFrom] = useState<string>('');
  const [auditTo, setAuditTo] = useState<string>('');
  const [auditClassFilter, setAuditClassFilter] = useState<string>('');

  const [auditStudentQ, setAuditStudentQ] = useState<string>('');
  const [auditStudentId, setAuditStudentId] = useState<number | null>(null);
  const [auditStudentOptions, setAuditStudentOptions] = useState<UserRow[]>([]);
  const [auditStudentLoading, setAuditStudentLoading] = useState(false);

  const [auditActorQ, setAuditActorQ] = useState<string>('');
  const [auditActorId, setAuditActorId] = useState<number | null>(null);
  const [auditActorOptions, setAuditActorOptions] = useState<UserRow[]>([]);
  const [auditActorLoading, setAuditActorLoading] = useState(false);

  const [auditLimit, setAuditLimit] = useState<number>(10);
  const [auditOffset, setAuditOffset] = useState<number>(0);
  const [auditExportCsvLoading, setAuditExportCsvLoading] = useState(false);
  const [auditExportPdfLoading, setAuditExportPdfLoading] = useState(false);

  const [reportsDays, setReportsDays] = useState(30);
  const [reportsLimit, setReportsLimit] = useState(200);
  const [reports, setReports] = useState<ReportsResponse | null>(null);
  const [reportsLoading, setReportsLoading] = useState(false);

  const [classRatings, setClassRatings] = useState<ClassRatingsResponse | null>(null);
  const [classRatingsLoading, setClassRatingsLoading] = useState(false);

  const [absDays, setAbsDays] = useState(7);
  const [absByClass, setAbsByClass] = useState<AbsencesByClassResponse | null>(null);
  const [absByClassLoading, setAbsByClassLoading] = useState(false);

  const [attendanceToday, setAttendanceToday] = useState<AttendanceTodayResponse | null>(null);
  const [attendanceTodayLoading, setAttendanceTodayLoading] = useState(false);

  const [teacherKpisDays, setTeacherKpisDays] = useState(30);
  const [teacherKpis, setTeacherKpis] = useState<TeacherKpisResponse | null>(null);
  const [teacherKpisLoading, setTeacherKpisLoading] = useState(false);

  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [roleFilter, setRoleFilter] = useState('');
  const [classFilter, setClassFilter] = useState<string>('');
  const [includeGraduatedUsers, setIncludeGraduatedUsers] = useState(false);
  const [q, setQ] = useState('');

  const PAGE_SIZE = 10;
  const [usersPage, setUsersPage] = useState(1);
  const [reportsPage, setReportsPage] = useState(1);

  const [secretariatUsers, setSecretariatUsers] = useState<SecretariatUserRow[]>([]);
  const [secretariatLoading, setSecretariatLoading] = useState(false);
  const [secretariatCreateLoading, setSecretariatCreateLoading] = useState(false);
  const [secretariatNewName, setSecretariatNewName] = useState('');
  const [secretariatNewEmail, setSecretariatNewEmail] = useState('');

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const resp = await api.get('/api/admin/school/summary');
      setSummary(resp.data as any);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const resp = await api.get('/api/admin/school/users', {
        params: {
          role: roleFilter || undefined,
          classroom_id: classFilter ? Number(classFilter) : undefined,
          q: q || undefined,
          include_graduated: includeGraduatedUsers ? 1 : 0,
        },
      });
      setUsers(Array.isArray(resp.data) ? resp.data : []);
      setUsersPage(1);
    } finally {
      setUsersLoading(false);
    }
  }, [classFilter, includeGraduatedUsers, q, roleFilter]);

  const loadSecretariatUsers = useCallback(async () => {
    setSecretariatLoading(true);
    try {
      const resp = await api.get('/api/admin/school/secretariat-users');
      setSecretariatUsers(Array.isArray(resp.data) ? (resp.data as any) : []);
    } finally {
      setSecretariatLoading(false);
    }
  }, []);

  const loadTeachers = useCallback(async () => {
    setTeachersLoading(true);
    try {
      const resp = await api.get('/api/admin/school/teachers');
      setTeachers(Array.isArray(resp.data) ? (resp.data as any) : []);
    } catch {
      setTeachers([]);
    } finally {
      setTeachersLoading(false);
    }
  }, []);

  const loadClasses = useCallback(async () => {
    setClassesLoading(true);
    setClassesError(null);
    try {
      const resp = await api.get('/api/admin/school/classes');
      setClasses(resp.data as any);
    } catch (e: any) {
      setClasses(null);
      setClassesError(e?.response?.data?.detail || e?.message || 'Nu am putut încărca lista de clase.');
    } finally {
      setClassesLoading(false);
    }
  }, []);

  const submitCreateTeacher = useCallback(async () => {
    const email = String(createTeacherForm.email || '').trim();
    const full_name = String(createTeacherForm.full_name || '').trim();
    const phone = String(createTeacherForm.phone || '').trim();
    if (!email || !email.includes('@') || !full_name) {
      alert('Completeaza nume + email valid.');
      return;
    }
    setCreateTeacherLoading(true);
    try {
      await api.post('/api/admin/school/teachers', { email, full_name, phone: phone || null });
      setCreateTeacherOpen(false);
      setCreateTeacherForm({ full_name: '', email: '', phone: '' });
      await loadTeachers();
      await loadUsers();
      alert('Profesor creat. A fost trimis email-ul de acces.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut crea profesorul.');
    } finally {
      setCreateTeacherLoading(false);
    }
  }, [createTeacherForm.email, createTeacherForm.full_name, createTeacherForm.phone, loadTeachers, loadUsers]);

  const submitCreateClass = useCallback(async () => {
    const name = String(createClassName || '').trim();
    if (!name) {
      alert('Introdu numele clasei.');
      return;
    }
    const admin_professor_id = createClassAdminTeacherId ? Number(createClassAdminTeacherId) : null;
    setCreateClassLoading(true);
    try {
      await api.post('/api/admin/school/classes', { name, admin_professor_id });
      setCreateClassOpen(false);
      setCreateClassName('');
      setCreateClassAdminTeacherId('');
      await loadClasses();
      alert('Clasa a fost creata.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut crea clasa.');
    } finally {
      setCreateClassLoading(false);
    }
  }, [createClassAdminTeacherId, createClassName, loadClasses]);

  const openSubjectsForClassroom = useCallback(
    async (classroomId: number, classroomName: string) => {
      setSubjectsClassroomId(classroomId);
      setSubjectsClassroomName(classroomName);
      setSubjectsOpen(true);
      setSubjectsError('');
      setSubjects([]);
      if (!teachers.length) await loadTeachers();
      setSubjectsLoading(true);
      try {
        const resp = await api.get('/api/admin/school/subjects', { params: { classroom_id: classroomId } });
        setSubjects(Array.isArray(resp.data) ? (resp.data as any) : []);
      } catch (e: any) {
        setSubjectsError(e?.response?.data?.detail || 'Nu am putut incarca materiile.');
        setSubjects([]);
      } finally {
        setSubjectsLoading(false);
      }
    },
    [loadTeachers, teachers.length]
  );

  const createSubject = useCallback(async () => {
    if (!subjectsClassroomId) return;
    const name = String(newSubjectName || '').trim();
    const professor_id = newSubjectTeacherId ? Number(newSubjectTeacherId) : null;
    if (!name || !professor_id) {
      alert('Completeaza nume materie + profesor.');
      return;
    }
    setNewSubjectLoading(true);
    try {
      await api.post('/api/admin/school/subjects', { classroom_id: subjectsClassroomId, name, professor_id });
      const resp = await api.get('/api/admin/school/subjects', { params: { classroom_id: subjectsClassroomId } });
      setSubjects(Array.isArray(resp.data) ? (resp.data as any) : []);
      setNewSubjectName('');
      setNewSubjectTeacherId('');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut crea materia.');
    } finally {
      setNewSubjectLoading(false);
    }
  }, [newSubjectName, newSubjectTeacherId, subjectsClassroomId]);

  const updateSubjectTeacher = useCallback(
    async (subjectId: number, professorId: number) => {
      try {
        await api.put(`/api/admin/school/subjects/${subjectId}`, { professor_id: professorId });
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut actualiza profesorul.');
        return;
      }
      if (subjectsClassroomId) {
        const resp = await api.get('/api/admin/school/subjects', { params: { classroom_id: subjectsClassroomId } });
        setSubjects(Array.isArray(resp.data) ? (resp.data as any) : []);
      }
    },
    [subjectsClassroomId]
  );

  const deleteSubject = useCallback(
    async (subjectId: number) => {
      const ok = window.confirm('Stergi materia? (doar daca nu are note/absente)');
      if (!ok) return;
      try {
        await api.delete(`/api/admin/school/subjects/${subjectId}`);
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut sterge materia.');
        return;
      }
      if (subjectsClassroomId) {
        const resp = await api.get('/api/admin/school/subjects', { params: { classroom_id: subjectsClassroomId } });
        setSubjects(Array.isArray(resp.data) ? (resp.data as any) : []);
      }
    },
    [subjectsClassroomId]
  );

  const auditEntityOptions = useMemo(
    () => [
      { value: '', label: 'Toate' },
      { value: 'grade', label: 'Note' },
      { value: 'absence', label: 'Absente' },
    ],
    []
  );

  const auditPageSizeOptions = useMemo(
    () => [
      { value: '10', label: '10 / pagina' },
      { value: '25', label: '25 / pagina' },
      { value: '50', label: '50 / pagina' },
      { value: '100', label: '100 / pagina' },
    ],
    []
  );

  const _isoRange = useCallback((dateStr: string, endOfDay: boolean) => {
    const d = String(dateStr || '').trim();
    if (!d) return undefined;
    return `${d}T${endOfDay ? '23:59:59' : '00:00:00'}`;
  }, []);

  const loadAuditEvents = useCallback(async (overrideOffset?: number) => {
    setAuditLoading(true);
    setAuditError('');
    try {
      const off = typeof overrideOffset === 'number' ? overrideOffset : auditOffset || 0;
      const params: any = {
        entity_type: auditEntityType || undefined,
        action: auditAction || undefined,
        student_id: auditStudentId || undefined,
        actor_user_id: auditActorId || undefined,
        classroom_id: auditClassFilter ? Number(auditClassFilter) : undefined,
        date_from: _isoRange(auditFrom, false),
        date_to: _isoRange(auditTo, true),
        limit: Math.max(1, auditLimit || 10),
        offset: off,
      };
      const resp = await api.get('/api/admin/school/audit/events', { params });
      setAuditEvents(Array.isArray(resp.data) ? (resp.data as any) : []);
    } catch (e: any) {
      setAuditError(e?.response?.data?.detail || 'Nu am putut incarca auditul.');
      setAuditEvents([]);
    } finally {
      setAuditLoading(false);
    }
  }, [
    auditAction,
    auditActorId,
    auditClassFilter,
    auditEntityType,
    auditFrom,
    auditLimit,
    auditOffset,
    auditStudentId,
    auditTo,
    _isoRange,
  ]);

  const exportAuditCsv = useCallback(async () => {
    setAuditExportCsvLoading(true);
    try {
      const params: any = {
        entity_type: auditEntityType || undefined,
        action: auditAction || undefined,
        student_id: auditStudentId || undefined,
        actor_user_id: auditActorId || undefined,
        classroom_id: auditClassFilter ? Number(auditClassFilter) : undefined,
        date_from: _isoRange(auditFrom, false),
        date_to: _isoRange(auditTo, true),
        limit: 5000,
        offset: 0,
      };

      const resp = await api.get('/api/admin/school/audit/events.csv', {
        params,
        responseType: 'blob',
      });

      const cd = String((resp.headers as any)?.['content-disposition'] || '');
      const m = cd.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `audit_${new Date().toISOString().slice(0, 10)}.csv`;
      const filename = (m?.[1] || '').trim() || fallbackName;

      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut exporta CSV.');
    } finally {
      setAuditExportCsvLoading(false);
    }
  }, [auditAction, auditActorId, auditClassFilter, auditEntityType, auditFrom, auditStudentId, auditTo, _isoRange]);

  const exportAuditPdf = useCallback(async () => {
    setAuditExportPdfLoading(true);
    try {
      const params: any = {
        entity_type: auditEntityType || undefined,
        action: auditAction || undefined,
        student_id: auditStudentId || undefined,
        actor_user_id: auditActorId || undefined,
        classroom_id: auditClassFilter ? Number(auditClassFilter) : undefined,
        date_from: _isoRange(auditFrom, false),
        date_to: _isoRange(auditTo, true),
        limit: 5000,
        offset: 0,
      };

      const resp = await api.get('/api/admin/school/audit/events', { params });
      const rows: AuditEvent[] = Array.isArray(resp.data) ? (resp.data as any) : [];

      const esc = (v: any) =>
        String(v == null ? '' : v)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\"/g, '&quot;')
          .replace(/'/g, '&#39;');

      const labelType = (t?: string | null) => (t === 'grade' ? 'Nota' : t === 'absence' ? 'Absenta' : t || '—');
      const labelChange = (ev: AuditEvent) => {
        const oldV = (ev as any)?.old_data?.value ?? (ev as any)?.old_data?.qualifier ?? (ev as any)?.old_data?.justified ?? '—';
        const newV = (ev as any)?.new_data?.value ?? (ev as any)?.new_data?.qualifier ?? (ev as any)?.new_data?.justified ?? '—';
        return `${oldV} → ${newV}`;
      };
      const labelSubject = (ev: AuditEvent) =>
        String((ev as any)?.new_data?.subject_name || (ev as any)?.old_data?.subject_name || (ev as any)?.new_data?.subject_id || (ev as any)?.old_data?.subject_id || '').trim();

      const title = 'Audit – note & absențe';
      const subtitle = `Generat: ${new Date().toLocaleString('ro-RO')}`;

      const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>
      body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"; padding: 20px; color: #111827; }
      h1 { font-size: 18px; margin: 0 0 6px; }
      .sub { color: #6b7280; font-size: 12px; margin: 0 0 14px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #e5e7eb; padding: 8px; font-size: 12px; vertical-align: top; }
      th { background: #f9fafb; text-align: left; }
      .muted { color: #6b7280; }
      @media print { body { padding: 0; } }
    </style>
  </head>
  <body>
    <h1>${esc(title)}</h1>
    <div class="sub">${esc(subtitle)} • Total: ${rows.length}</div>
    <table>
      <thead>
        <tr>
          <th>Data</th>
          <th>Tip</th>
          <th>Elev</th>
          <th>Actiune</th>
          <th>Actor</th>
          <th>IP</th>
          <th>Detalii</th>
        </tr>
      </thead>
      <tbody>
        ${rows
          .map((ev) => {
            const dt = (ev as any)?.created_at ? new Date((ev as any).created_at).toLocaleString('ro-RO') : '—';
            const type = labelType((ev as any)?.entity_type);
            const student = (ev as any)?.subject_name || '';
            const action = (ev as any)?.action || '—';
            const actor = (ev as any)?.actor_name || '—';
            const ip = (ev as any)?.actor_ip || '—';
            const subj = labelSubject(ev);
            const details = `${subj ? `Materie: ${subj} • ` : ''}ID: #${(ev as any)?.entity_id ?? ''} • Schimbare: ${labelChange(ev)}`;
            return `<tr>
              <td class="muted">${esc(dt)}</td>
              <td>${esc(type)}</td>
              <td>${esc(student || '—')}</td>
              <td class="muted">${esc(action)}</td>
              <td class="muted">${esc(actor)}</td>
              <td class="muted">${esc(ip)}</td>
              <td>${esc(details)}</td>
            </tr>`;
          })
          .join('')}
      </tbody>
    </table>
    <script>
      setTimeout(() => { try { window.focus(); window.print(); } catch (e) {} }, 250);
    </script>
  </body>
</html>`;

      const w = window.open('', '_blank', 'noopener,noreferrer');
      if (!w) {
        alert('Browserul a blocat fereastra de print. Permite pop-up pentru export PDF.');
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut exporta PDF.');
    } finally {
      setAuditExportPdfLoading(false);
    }
  }, [auditAction, auditActorId, auditClassFilter, auditEntityType, auditFrom, auditStudentId, auditTo, _isoRange]);

  const createSecretariatUser = useCallback(async () => {
    const email = secretariatNewEmail.trim().toLowerCase();
    const full_name = secretariatNewName.trim();
    if (!email) {
      alert('Email obligatoriu.');
      return;
    }
    setSecretariatCreateLoading(true);
    try {
      await api.post('/api/admin/school/secretariat-users', { email, full_name: full_name || undefined });
      setSecretariatNewEmail('');
      setSecretariatNewName('');
      await loadSecretariatUsers();
      await loadUsers();
      alert('Cont secretariat creat. Parola temporară a fost trimisă pe email și trebuie schimbată la primul login.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut crea contul.');
    } finally {
      setSecretariatCreateLoading(false);
    }
  }, [loadSecretariatUsers, loadUsers, secretariatNewEmail, secretariatNewName]);

  const resendSecretariatInvite = useCallback(
    async (u: SecretariatUserRow) => {
      const ok = window.confirm(`Retrimiți invitația (parolă nouă) pentru ${u.full_name} (${u.email})?`);
      if (!ok) return;
      try {
        await api.post(`/api/admin/school/secretariat-users/${u.id}/resend-invite`);
        alert('Invitația a fost retrimisă pe email.');
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut retrimite invitația.');
      }
    },
    []
  );

  const deleteSecretariatUser = useCallback(
    async (u: SecretariatUserRow) => {
      const ok = window.confirm(`Ștergi contul de secretariat pentru ${u.full_name} (${u.email})?`);
      if (!ok) return;
      try {
        await api.delete(`/api/admin/school/secretariat-users/${u.id}`);
        await loadSecretariatUsers();
        await loadUsers();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut șterge contul.');
      }
    },
    [loadSecretariatUsers, loadUsers]
  );

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    try {
      const resp = await api.get('/api/admin/school/reports/at-risk-students', {
        params: { days: reportsDays, limit: reportsLimit },
      });
      setReports(resp.data as any);
      setReportsPage(1);
    } finally {
      setReportsLoading(false);
    }
  }, [reportsDays, reportsLimit]);

  const loadClassRatings = useCallback(async () => {
    setClassRatingsLoading(true);
    try {
      const resp = await api.get('/api/admin/school/reports/class-ratings', { params: { limit: 1000 } });
      setClassRatings(resp.data as any);
    } finally {
      setClassRatingsLoading(false);
    }
  }, []);

  const loadAbsencesByClass = useCallback(async () => {
    setAbsByClassLoading(true);
    try {
      const resp = await api.get('/api/admin/school/reports/unjustified-absences-by-class', {
        params: { days: absDays, limit: 1000 },
      });
      setAbsByClass(resp.data as any);
    } finally {
      setAbsByClassLoading(false);
    }
  }, [absDays]);

  const loadAttendanceToday = useCallback(async () => {
    setAttendanceTodayLoading(true);
    try {
      const resp = await api.get('/api/admin/school/reports/attendance-today');
      setAttendanceToday(resp.data as any);
    } finally {
      setAttendanceTodayLoading(false);
    }
  }, []);

  const loadTeacherKpis = useCallback(async () => {
    setTeacherKpisLoading(true);
    try {
      const resp = await api.get('/api/admin/school/reports/teacher-kpis', {
        params: { days: teacherKpisDays, limit: 1000 },
      });
      setTeacherKpis(resp.data as any);
    } finally {
      setTeacherKpisLoading(false);
    }
  }, [teacherKpisDays]);

  const downloadCsv = useCallback((filename: string, rows: Array<Record<string, any>>) => {
    const headers = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      if (/[\",\\n\\r]/.test(s)) return `\"${s.replace(/\"/g, '\"\"')}\"`;
      return s;
    };
    const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape((r as any)[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, []);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const resp = await api.get('/api/admin/school/settings');
      setSettings(resp.data as any);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const saveSettings = useCallback(async () => {
    if (!settings) return;
    setSettingsSaving(true);
    try {
      await api.put('/api/admin/school/settings', {
        absence_penalty_per_student: settings.absence_penalty_per_student,
        absence_penalty_cap: settings.absence_penalty_cap,
        unjustified_absences_7d_threshold: settings.unjustified_absences_7d_threshold,
        auto_notifications_enabled: settings.auto_notifications_enabled,
        auto_notifications_frequency: settings.auto_notifications_frequency,
        current_school_year: settings.current_school_year ?? null,
        current_structure: settings.current_structure ?? null,
        current_education_level: settings.current_education_level ?? null,
      });
      await loadSettings();
      await loadSummary();
      alert('Setările au fost salvate.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut salva setările.');
    } finally {
      setSettingsSaving(false);
    }
  }, [loadSettings, loadSummary, settings]);

  const publishAlertsAsNotifications = useCallback(async () => {
    setPublishingAlerts(true);
    try {
      const resp = await api.post('/api/admin/school/alerts/publish');
      const created = Number(resp.data?.created || 0);
      const skipped = Number(resp.data?.skipped || 0);
      alert(`Notificari create: ${created}${skipped ? ` (duplicate sarite: ${skipped})` : ''}`);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut publica notificarile.');
    } finally {
      setPublishingAlerts(false);
    }
  }, []);

  useEffect(() => {
    const sp = new URLSearchParams(location.search || '');
    const t = (sp.get('tab') || '').trim().toLowerCase();
    const next: TabKey =
      t === 'users'
        ? 'users'
        : t === 'classes'
        ? 'classes'
        : t === 'reports'
        ? 'reports'
        : t === 'audit'
        ? 'audit'
        : t === 'settings'
        ? 'settings'
        : 'overview';
    setTab(next);
  }, [location.search]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (tab === 'users') void loadUsers();
    if (tab === 'classes') {
      if (!classes && !classesLoading && !classesError) void loadClasses();
    }
    if (tab === 'reports') void loadReports();
    if (tab === 'audit') {
      if (!classes) void loadClasses();
      void loadAuditEvents();
    }
    if (tab === 'settings') void loadSettings();
  }, [classes, classesError, classesLoading, loadAuditEvents, loadClasses, loadReports, loadSettings, loadUsers, tab]);

  useEffect(() => {
    if (tab !== 'users') return;
    if (!classes) void loadClasses();
    void loadSecretariatUsers();
  }, [classes, loadClasses, loadSecretariatUsers, tab]);

  useEffect(() => {
    if (tab !== 'reports') return;
    void loadClassRatings();
    void loadAbsencesByClass();
    void loadTeacherKpis();
  }, [loadAbsencesByClass, loadClassRatings, loadTeacherKpis, tab]);

  useEffect(() => {
    if (tab !== 'overview') return;
    void loadAttendanceToday();
  }, [loadAttendanceToday, tab]);

  const roleOptions = useMemo(
    () => [
      { value: '', label: 'Toate rolurile' },
      { value: 'profesor', label: 'Profesori' },
      { value: 'elev', label: 'Elevi' },
      { value: 'parinte', label: 'Părinți' },
      { value: 'secretariat', label: 'Secretariat' },
      { value: 'admin', label: 'Admin' },
    ],
    []
  );

  const classOptions = useMemo(() => {
    const base = [{ value: '', label: 'Toate clasele' }];
    const items = (classes?.school_classes || []).map((c) => ({ value: String(c.id), label: c.name }));
    return base.concat(items);
  }, [classes?.school_classes]);

  const teacherOptions = useMemo(() => {
    const base = [{ value: '', label: 'Selecteaza profesor' }];
    const items = (teachers || []).map((t) => ({
      value: String(t.id),
      label: `${t.full_name || `Profesor ${t.id}`}${t.email ? ` â€¢ ${t.email}` : ''}`,
    }));
    return base.concat(items);
  }, [teachers]);

  const auditStudentSelectOptions = useMemo(() => {
    const base = [{ value: '', label: 'Toti elevii' }];
    const items = (auditStudentOptions || []).map((u) => ({
      value: String(u.id),
      label: `${u.full_name}${u.email ? ` • ${u.email}` : ''}`,
    }));
    return base.concat(items);
  }, [auditStudentOptions]);

  const auditActorSelectOptions = useMemo(() => {
    const base = [{ value: '', label: 'Toata lumea' }];
    const items = (auditActorOptions || []).map((u) => ({
      value: String(u.id),
      label: `${u.full_name}${u.role ? ` • ${u.role}` : ''}`,
    }));
    return base.concat(items);
  }, [auditActorOptions]);

  const usersTotalPages = useMemo(() => Math.max(1, Math.ceil((users?.length || 0) / PAGE_SIZE)), [PAGE_SIZE, users?.length]);
  const usersPageSafe = useMemo(() => Math.min(Math.max(1, usersPage), usersTotalPages), [usersPage, usersTotalPages]);
  useEffect(() => {
    if (usersPage !== usersPageSafe) setUsersPage(usersPageSafe);
  }, [usersPage, usersPageSafe]);
  const pagedUsers = useMemo(() => {
    const start = (usersPageSafe - 1) * PAGE_SIZE;
    return (users || []).slice(start, start + PAGE_SIZE);
  }, [PAGE_SIZE, users, usersPageSafe]);

  const reportItems = useMemo(() => (reports?.items || []) as any[], [reports]);
  const reportsTotalPages = useMemo(() => Math.max(1, Math.ceil((reportItems?.length || 0) / PAGE_SIZE)), [PAGE_SIZE, reportItems?.length]);
  const reportsPageSafe = useMemo(() => Math.min(Math.max(1, reportsPage), reportsTotalPages), [reportsPage, reportsTotalPages]);
  useEffect(() => {
    if (reportsPage !== reportsPageSafe) setReportsPage(reportsPageSafe);
  }, [reportsPage, reportsPageSafe]);
  const pagedReportItems = useMemo(() => {
    const start = (reportsPageSafe - 1) * PAGE_SIZE;
    return (reportItems || []).slice(start, start + PAGE_SIZE);
  }, [PAGE_SIZE, reportItems, reportsPageSafe]);

  const auditHasMore = useMemo(() => (auditEvents?.length || 0) >= Math.max(1, auditLimit || 10), [auditEvents?.length, auditLimit]);
  const auditPage = useMemo(() => Math.floor((auditOffset || 0) / Math.max(1, auditLimit || 10)) + 1, [auditLimit, auditOffset]);

  const suspendToggle = async (u: UserRow) => {
    const next = !u.is_suspended;
    const ok = window.confirm(`${next ? 'Suspendă' : 'Reactivează'} contul pentru ${u.full_name}?`);
    if (!ok) return;
    try {
      await api.post(`/api/admin/school/users/${u.id}/suspend`, { is_suspended: next });
      await loadUsers();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut actualiza statusul.');
    }
  };

  const openUserDetails = useCallback(async (userId: number) => {
    setUserModalOpen(true);
    setUserDetailsLoading(true);
    setSelectedUserDetails(null);
    setUserEditMode(false);
    setUserEditSaving(false);
    setUserExportLoading(false);
    setUserDeleteLoading(false);
    setUserGraduateLoading(false);
    setStudentGradeAudit([]);
    setStudentGradeAuditError('');
    setStudentGradeAuditLoading(false);
    try {
      const resp = await api.get(`/api/admin/school/users/${userId}`);
      setSelectedUserDetails(resp.data as any);
      setUserEditForm({
        full_name: String((resp.data as any)?.full_name || ''),
        email: String((resp.data as any)?.email || ''),
        phone: String((resp.data as any)?.phone || ''),
        address: String((resp.data as any)?.address || ''),
      });

      const role = String((resp.data as any)?.role || '').toLowerCase();
      if (role === 'elev') {
        setStudentGradeAuditLoading(true);
        try {
          const a = await api.get('/api/admin/school/audit/grades', { params: { student_id: userId, limit: 200 } });
          setStudentGradeAudit(Array.isArray(a.data) ? (a.data as any) : []);
        } catch (e: any) {
          setStudentGradeAuditError(e?.response?.data?.detail || 'Nu am putut incarca auditul pentru note.');
          setStudentGradeAudit([]);
        } finally {
          setStudentGradeAuditLoading(false);
        }
      }
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut încărca detaliile utilizatorului.');
      setUserModalOpen(false);
    } finally {
      setUserDetailsLoading(false);
    }
  }, []);

  const closeUserModal = useCallback(() => {
    setUserModalOpen(false);
    setSelectedUserDetails(null);
    setUserDetailsLoading(false);
    setUserEditMode(false);
    setUserEditSaving(false);
    setUserExportLoading(false);
    setUserDeleteLoading(false);
    setStudentGradeAudit([]);
    setStudentGradeAuditError('');
    setStudentGradeAuditLoading(false);
  }, []);

  const saveUserEdits = useCallback(async () => {
    if (!selectedUserDetails) return;
    setUserEditSaving(true);
    try {
      const payload: any = {
        full_name: userEditForm.full_name,
        phone: userEditForm.phone,
        address: userEditForm.address,
      };
      const email = (userEditForm.email || '').trim();
      if (email) payload.email = email;
      await api.put(`/api/admin/school/users/${selectedUserDetails.id}`, payload);
      setUserEditMode(false);
      await openUserDetails(selectedUserDetails.id);
      await loadUsers();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut salva modificarile.');
    } finally {
      setUserEditSaving(false);
    }
  }, [loadUsers, openUserDetails, selectedUserDetails, userEditForm.address, userEditForm.email, userEditForm.full_name, userEditForm.phone]);

  const exportSelectedUserJson = useCallback(async () => {
    if (!selectedUserDetails) return;
    setUserExportLoading(true);
    try {
      const resp = await api.get(`/api/admin/school/users/${selectedUserDetails.id}/export.json`, { responseType: 'blob' });
      const cd = String((resp.headers as any)?.['content-disposition'] || '');
      const m = cd.match(/filename=\"?([^\";]+)\"?/i);
      const fallbackName = `user_${selectedUserDetails.id}_export.json`;
      const filename = (m?.[1] || '').trim() || fallbackName;
      const blob = new Blob([resp.data], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut exporta datele.');
    } finally {
      setUserExportLoading(false);
    }
  }, [selectedUserDetails]);

  const deleteSelectedUser = useCallback(async () => {
    if (!selectedUserDetails) return;
    const role = String(selectedUserDetails.role || '').toLowerCase();
    if (role === 'admin' || role === 'profesor') {
      alert('Stergerea este blocata pentru admin/profesor. Foloseste Suspendare.');
      return;
    }
    const ok = window.confirm(`Soft-delete pentru ${selectedUserDetails.full_name}? Contul va fi dezactivat si anonimizat, iar datele raman in platforma.`);
    if (!ok) return;
    setUserDeleteLoading(true);
    try {
      await api.delete(`/api/admin/school/users/${selectedUserDetails.id}`);
      closeUserModal();
      await loadUsers();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut sterge contul.');
    } finally {
      setUserDeleteLoading(false);
    }
  }, [closeUserModal, loadUsers, selectedUserDetails]);

  const graduateSelectedUser = useCallback(async () => {
    if (!selectedUserDetails) return;
    const role = String(selectedUserDetails.role || '').toLowerCase();
    if (role !== 'elev') {
      alert('Absolvirea este disponibila doar pentru elevi.');
      return;
    }
    if (selectedUserDetails.is_graduated) {
      alert('Acest cont este deja arhivat (absolvit).');
      return;
    }

    const ok = window.confirm(
      `Absolvire pentru ${selectedUserDetails.full_name}?\n\nContul va fi arhivat: login blocat, sesiuni/dispozitive revocate. Datele raman pentru audit.`
    );
    if (!ok) return;

    setUserGraduateLoading(true);
    try {
      await api.post(`/api/admin/school/users/${selectedUserDetails.id}/graduate`, {
        school_year: settings?.current_school_year || undefined,
        reason: 'manual',
      });
      await openUserDetails(selectedUserDetails.id);
      await loadUsers();
      alert('Absolvire efectuata. Contul a fost arhivat.');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu am putut efectua absolvirea.');
    } finally {
      setUserGraduateLoading(false);
    }
  }, [loadUsers, openUserDetails, selectedUserDetails, settings?.current_school_year]);

  const graduateClassById = useCallback(
    async (classId: number, className: string) => {
      const ok = window.confirm(
        `Absolvire pentru clasa \"${className}\"?\n\nToti elevii din clasa vor fi arhivati (login blocat, sesiuni/dispozitive revocate).`
      );
      if (!ok) return;

      setClassGraduateLoading((p) => ({ ...p, [classId]: true }));
      try {
        const resp = await api.post(`/api/admin/school/classrooms/${classId}/graduate`, {
          school_year: settings?.current_school_year || undefined,
          reason: 'bulk',
          archive_class: true,
        });
        const count = Number(resp.data?.graduated_users || 0);
        alert(`Absolvire efectuata: ${count} elevi arhivati.`);
        await loadClasses();
        await loadUsers();
      } catch (e: any) {
        alert(e?.response?.data?.detail || 'Nu am putut efectua absolvirea clasei.');
      } finally {
        setClassGraduateLoading((p) => ({ ...p, [classId]: false }));
      }
    },
    [loadClasses, loadUsers, settings?.current_school_year]
  );

  useEffect(() => {
    const q2 = auditStudentQ.trim();
    if (q2.length < 2) {
      setAuditStudentOptions([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setAuditStudentLoading(true);
      try {
        const r = await api.get('/api/admin/school/users', { params: { role: 'elev', q: q2 } });
        const rows = Array.isArray(r.data) ? (r.data as any) : [];
        setAuditStudentOptions(rows.slice(0, 10));
      } catch {
        setAuditStudentOptions([]);
      } finally {
        setAuditStudentLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [auditStudentQ]);

  useEffect(() => {
    const q2 = auditActorQ.trim();
    if (q2.length < 2) {
      setAuditActorOptions([]);
      return;
    }
    const t = window.setTimeout(async () => {
      setAuditActorLoading(true);
      try {
        const r = await api.get('/api/admin/school/users', { params: { q: q2 } });
        const rows = (Array.isArray(r.data) ? (r.data as any) : []).filter((u: any) => {
          const role = String(u?.role || '').toLowerCase();
          return role === 'profesor' || role === 'admin' || role === 'secretariat';
        });
        setAuditActorOptions(rows.slice(0, 10));
      } catch {
        setAuditActorOptions([]);
      } finally {
        setAuditActorLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [auditActorQ]);

  const selectedStudentGradesById = useMemo(() => {
    const map = new Map<number, any>();
    const grades = (selectedUserDetails as any)?.catalog_full?.grades || [];
    for (const g of grades) {
      if (g && typeof g.id === 'number') map.set(g.id, g);
    }
    return map;
  }, [(selectedUserDetails as any)?.catalog_full?.grades]);

  const roleLabel = useCallback((r: string) => {
    const role = (r || '').toLowerCase();
    if (role === 'profesor') return 'Profesor';
    if (role === 'elev') return 'Elev';
    if (role === 'parinte') return 'Părinte';
    if (role === 'admin') return 'Admin';
    if (role === 'secretariat') return 'Secretariat';
    return r || '—';
  }, []);

  const goTab = useCallback(
    (t: TabKey) => {
      if (t === 'overview') navigate('/platform-admin-dashboard');
      else navigate(`/platform-admin-dashboard?tab=${t}`);
    },
    [navigate]
  );

  return (
    <>
      <Helmet>
        <title>Admin școală - Scola</title>
      </Helmet>

      <Header user={user} onNavigate={(p) => navigate(p)} onSignOut={() => signOut()} />

      <div className="min-h-screen bg-background pt-16">
        {showSidebar ? (
          <DashboardSidebar
            user={{ role: 'admin' }}
            isCollapsed={isSidebarCollapsed}
            onNavigate={(path) => navigate(path)}
          />
        ) : null}

        <main className={`transition-all duration-300 ${showSidebar ? (isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64') : ''}`}>
          <div className="p-6 max-w-6xl mx-auto">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
              <div className="flex items-center gap-3">
                {showSidebar ? (
                  <button
                    onClick={() => setIsSidebarCollapsed((p) => !p)}
                    className="hidden lg:flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-colors"
                    aria-label="Comută bara laterală"
                  >
                    <Icon name={isSidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
                  </button>
                ) : null}
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Administrare școală</h1>
                  <p className="text-sm text-muted-foreground">
                    {summary?.school ? `${summary.school.name} (${summary.school.county}, ${summary.school.city})` : '—'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" iconName="RefreshCw" onClick={() => void loadSummary()} disabled={summaryLoading}>
                  Reîncarcă
                </Button>
                <Button variant="outline" iconName="FileText" onClick={() => navigate('/documents')}>
                  Documente
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              {([
                { id: 'overview', label: 'Prezentare' },
                { id: 'users', label: 'Utilizatori' },
                { id: 'classes', label: 'Clase' },
                { id: 'reports', label: 'Rapoarte' },
                { id: 'settings', label: 'Setări' },
              ] as Array<{ id: TabKey; label: string }>).map((t) => (
                <button
                  key={t.id}
                  onClick={() => goTab(t.id)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border ${
                    tab === t.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'overview' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-card border border-border rounded-lg p-5">
                  <div className="text-xs text-muted-foreground">Profesori</div>
                  <div className="text-2xl font-bold text-foreground mt-1">{summary?.counts.teachers ?? 0}</div>
                </div>
                <div className="bg-card border border-border rounded-lg p-5">
                  <div className="text-xs text-muted-foreground">Elevi</div>
                  <div className="text-2xl font-bold text-foreground mt-1">{summary?.counts.students ?? 0}</div>
                </div>
                <div className="bg-card border border-border rounded-lg p-5">
                  <div className="text-xs text-muted-foreground">Părinți</div>
                  <div className="text-2xl font-bold text-foreground mt-1">{summary?.counts.parents ?? 0}</div>
                </div>
                <div className="bg-card border border-border rounded-lg p-5">
                  <div className="text-xs text-muted-foreground">Clase</div>
                  <div className="text-2xl font-bold text-foreground mt-1">{summary?.counts.classes ?? 0}</div>
                </div>

                <div className="bg-card border border-border rounded-lg p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs text-muted-foreground">Absenți azi (elevi)</div>
                      <div className="text-2xl font-bold text-foreground mt-1">
                        {attendanceTodayLoading ? '…' : attendanceToday ? attendanceToday.absent_students : '—'}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {attendanceToday ? `Total elevi: ${attendanceToday.students_total} • Data: ${attendanceToday.date}` : '—'}
                      </div>
                    </div>
                    <Icon name="UserX" size={18} className="text-muted-foreground mt-1" />
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs text-muted-foreground">Absențe nemotivate azi</div>
                      <div className="text-2xl font-bold text-foreground mt-1">
                        {attendanceTodayLoading ? '…' : attendanceToday ? attendanceToday.absences_unjustified : '—'}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {attendanceToday ? `Absențe totale azi: ${attendanceToday.absences_total}` : '—'}
                      </div>
                    </div>
                    <Icon name="AlertTriangle" size={18} className="text-muted-foreground mt-1" />
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-5">
                  <div className="text-xs text-muted-foreground">Rating școală</div>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="text-2xl font-bold text-foreground">
                        {typeof summary?.rating?.rating_percent === 'number' ? `${summary?.rating?.rating_percent}%` : '—'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {typeof summary?.rating?.avg_grade === 'number' ? `Medie: ${summary?.rating?.avg_grade.toFixed(2)} / 10` : 'Fără note'}
                      </div>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${typeof summary?.rating?.rating_percent === 'number' ? 'bg-primary' : 'bg-muted'}`}
                        style={{ width: `${typeof summary?.rating?.rating_percent === 'number' ? summary?.rating?.rating_percent : 0}%` }}
                        role="progressbar"
                        aria-valuenow={typeof summary?.rating?.rating_percent === 'number' ? summary?.rating?.rating_percent : 0}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      />
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {typeof summary?.rating?.absences_unjustified_7d === 'number'
                        ? `Abs. nemotivate (7 zile): ${summary?.rating?.absences_unjustified_7d}`
                        : '—'}
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-5">
                  <div className="text-xs text-muted-foreground">Absențe nemotivate</div>
                  <div className="text-2xl font-bold text-foreground mt-1">{summary?.rating?.absences_unjustified ?? 0}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">Total absențe: {summary?.rating?.absences_total ?? 0}</div>
                </div>

                <div className="sm:col-span-2 lg:col-span-4 bg-card border border-border rounded-lg p-5">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div className="flex items-center gap-2">
                      <Icon name="Bell" size={16} className="text-muted-foreground" />
                      <div className="text-sm font-semibold text-foreground">Alerte</div>
                    </div>
                    {!isReadOnly ? (
                      <Button
                        variant="outline"
                        iconName="Send"
                        onClick={() => void publishAlertsAsNotifications()}
                        disabled={publishingAlerts}
                      >
                        Publică ca notificări
                      </Button>
                    ) : (
                      <div className="text-xs text-muted-foreground">Read-only</div>
                    )}
                  </div>
                  {(summary?.alerts || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">Nicio alertă.</div>
                  ) : (
                    <ul className="space-y-2">
                      {(summary?.alerts || []).slice(0, 8).map((a) => (
                        <li
                          key={a.key}
                          className="p-3 rounded-md border border-border bg-background flex items-start justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className={`w-2.5 h-2.5 rounded-full ${
                                  a.severity === 'high' ? 'bg-error' : a.severity === 'warning' ? 'bg-yellow-500' : 'bg-muted-foreground'
                                }`}
                                aria-hidden="true"
                              />
                              <div className="text-sm font-medium text-foreground">{a.title}</div>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">{a.message}</div>
                          </div>
                          {a.link ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="ArrowRight"
                              onClick={() => {
                                const l = String(a.link || '');
                                if (l.includes('tab=users')) setTab('users');
                                else if (l.includes('tab=classes')) setTab('classes');
                                else setTab('overview');
                              }}
                            >
                              Vezi
                            </Button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="sm:col-span-2 lg:col-span-4 bg-card border border-border rounded-lg p-5">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <div className="text-sm font-semibold text-foreground">Absențe azi pe clase (top)</div>
                      <div className="text-xs text-muted-foreground">Primele 10 clase după absențe nemotivate.</div>
                    </div>
                    <Button
                      variant="outline"
                      iconName="RefreshCw"
                      onClick={() => void loadAttendanceToday()}
                      disabled={attendanceTodayLoading}
                    >
                      Reîncarcă
                    </Button>
                  </div>
                  {!attendanceToday || (attendanceToday.by_class || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">Nu există absențe înregistrate azi.</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(attendanceToday.by_class || []).slice(0, 10).map((c) => (
                        <div key={c.id} className="p-3 rounded-md border border-border bg-background">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-medium text-foreground">{c.name}</div>
                            <div className="text-xs text-muted-foreground">
                              Nemotivate: <span className="text-foreground font-medium">{c.absences_unjustified}</span>
                            </div>
                          </div>
                          <div className="mt-1 text-[11px] text-muted-foreground">
                            Absenți (elevi): {c.absent_students} • Total absențe: {c.absences_total}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="sm:col-span-2 lg:col-span-4 bg-card border border-border rounded-lg p-5">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
                    <div className="text-sm text-muted-foreground">
                      Dashboardul este read-only. Pentru operațional (repartizări, importuri, promovări) vom adăuga un dashboard separat pentru secretariat.
                    </div>
                  </div>
                </div>

                <div className="sm:col-span-2 lg:col-span-4 bg-card border border-border rounded-lg p-5">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div>
                      <div className="font-semibold text-foreground">Profesori – KPI (monitorizare)</div>
                      <div className="text-xs text-muted-foreground">
                        Metrici obiective (note introduse, elevi acoperiți, absențe pe materiile predate).
                      </div>
                    </div>
                    <div className="flex items-end gap-2 flex-wrap">
                      <div className="w-[110px]">
                        <Input
                          label="Zile"
                          type="number"
                          min={1}
                          step="1"
                          value={teacherKpisDays}
                          onChange={(e) => setTeacherKpisDays(Math.max(1, Number(e.target.value) || 1))}
                        />
                      </div>
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadTeacherKpis()} disabled={teacherKpisLoading}>
                        Reîncarcă
                      </Button>
                      <Button
                        variant="outline"
                        iconName="Download"
                        onClick={() =>
                          downloadCsv(
                            `kpi_profesori_${teacherKpisDays}zile.csv`,
                            (teacherKpis?.items || []).map((t) => ({
                              id: t.id,
                              nume: t.full_name,
                              email: t.email,
                              materii: t.subjects_count,
                              clase: t.classes_taught,
                              note_introduse: t.grades_count,
                              elevi_acoperiti: t.students_touched,
                              medie_note: t.avg_grade ?? '',
                              absente_total_materii: t.absences_total,
                              absente_nemotivate_materii: t.absences_unjustified,
                            }))
                          )
                        }
                        disabled={teacherKpisLoading || (teacherKpis?.items || []).length === 0}
                      >
                        Export CSV
                      </Button>
                    </div>
                  </div>

                  {teacherKpisLoading ? (
                    <div className="p-4 text-sm text-muted-foreground">Se încarcă...</div>
                  ) : (teacherKpis?.items || []).length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">Nu există date.</div>
                  ) : (
                    <>
                      <div className="hidden md:block border border-border rounded-lg overflow-hidden">
                        <div className="max-h-[460px] overflow-auto">
                          <table className="min-w-[980px] w-full text-sm">
                            <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="p-3 text-left text-muted-foreground font-medium">Profesor</th>
                            <th className="p-3 text-right text-muted-foreground font-medium">Materii</th>
                            <th className="p-3 text-right text-muted-foreground font-medium">Clase</th>
                            <th className="p-3 text-right text-muted-foreground font-medium">Note</th>
                            <th className="p-3 text-right text-muted-foreground font-medium">Elevi</th>
                            <th className="p-3 text-right text-muted-foreground font-medium">Medie</th>
                            <th className="p-3 text-right text-muted-foreground font-medium">Abs. nemot.</th>
                          </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                          {(teacherKpis?.items || []).slice(0, 200).map((t) => (
                            <tr key={t.id} className="hover:bg-muted/30">
                              <td className="p-3">
                                <div className="text-foreground font-medium">{t.full_name}</div>
                                <div className="text-xs text-muted-foreground">{t.email}</div>
                              </td>
                              <td className="p-3 text-right text-foreground">{t.subjects_count ?? 0}</td>
                              <td className="p-3 text-right text-foreground">{t.classes_taught ?? 0}</td>
                              <td className="p-3 text-right text-foreground">{t.grades_count ?? 0}</td>
                              <td className="p-3 text-right text-foreground">{t.students_touched ?? 0}</td>
                              <td className="p-3 text-right text-foreground">{typeof t.avg_grade === 'number' ? t.avg_grade.toFixed(2) : '—'}</td>
                              <td className="p-3 text-right text-foreground">{t.absences_unjustified ?? 0}</td>
                            </tr>
                          ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="md:hidden space-y-2">
                        {(teacherKpis?.items || []).slice(0, 50).map((t) => (
                          <div key={t.id} className="bg-background border border-border rounded-lg p-3">
                            <div className="text-sm font-medium text-foreground">{t.full_name}</div>
                            <div className="text-xs text-muted-foreground">{t.email}</div>
                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground">Materii</span>
                                <span className="text-foreground font-medium">{t.subjects_count ?? 0}</span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground">Clase</span>
                                <span className="text-foreground font-medium">{t.classes_taught ?? 0}</span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground">Note</span>
                                <span className="text-foreground font-medium">{t.grades_count ?? 0}</span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground">Elevi</span>
                                <span className="text-foreground font-medium">{t.students_touched ?? 0}</span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground">Medie</span>
                                <span className="text-foreground font-medium">{typeof t.avg_grade === 'number' ? t.avg_grade.toFixed(2) : '—'}</span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground">Abs. nemot.</span>
                                <span className="text-foreground font-medium">{t.absences_unjustified ?? 0}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {(teacherKpis?.items || []).length > 50 ? (
                          <div className="text-xs text-muted-foreground">Pe mobil afișăm primele 50. Folosește Export CSV pentru toate.</div>
                        ) : null}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : null}

            {tab === 'users' ? (
              <>
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-sm font-semibold text-foreground">Conturi secretariat</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Creezi oricâte conturi de secretariat vrei. Parola este generată automat și trebuie schimbată la primul login.
                      </div>
                    </div>
                    <Button variant="outline" iconName="RefreshCw" onClick={() => void loadSecretariatUsers()} disabled={secretariatLoading}>
                      Reîncarcă
                    </Button>
                  </div>

                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <Input
                      label="Nume"
                      placeholder="Secretariat 1"
                      value={secretariatNewName}
                      onChange={(e) => setSecretariatNewName(e.target.value)}
                    />
                    <Input
                      label="Email"
                      placeholder="secretariat@școala.ro"
                      value={secretariatNewEmail}
                      onChange={(e) => setSecretariatNewEmail(e.target.value)}
                    />
                    <div className="flex items-end w-full">
                      <Button
                        variant="default"
                        iconName="UserPlus"
                        onClick={() => void createSecretariatUser()}
                        loading={secretariatCreateLoading}
                        fullWidth
                      >
                        Creează cont
                      </Button>
                    </div>
                  </div>

                  <div className="mt-4">
                    {secretariatLoading ? (
                      <div className="text-sm text-muted-foreground">Se încarcă...</div>
                    ) : secretariatUsers.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Nu există conturi de secretariat.</div>
                    ) : (
                      <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
                        {secretariatUsers.map((u) => (
                          <li
                            key={u.id}
                            className="p-3 bg-background flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                          >
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">{u.full_name}</div>
                              <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                            </div>
                            <div className="w-full sm:w-auto flex flex-col min-[420px]:flex-row items-stretch sm:items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                iconName="Send"
                                onClick={() => void resendSecretariatInvite(u)}
                                fullWidth
                              >
                                Retrimite
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                iconName="Trash2"
                                onClick={() => void deleteSecretariatUser(u)}
                                fullWidth
                              >
                                Șterge
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
                    <Input label="Caută" placeholder="Nume / email..." value={q} onChange={(e) => setQ(e.target.value)} />
                    <Select
                      label="Rol"
                      value={roleFilter}
                      options={roleOptions}
                      onChange={(v) => setRoleFilter(String(v))}
                      searchable
                      clearable
                    />
                    <Select
                      label="Clasă"
                      value={classFilter}
                      options={classOptions}
                      onChange={(v) => setClassFilter(String(v))}
                      searchable
                      clearable
                      disabled={!classes}
                    />
                    <div className="flex items-end">
                      <Button variant="default" iconName="Search" onClick={() => void loadUsers()} disabled={usersLoading}>
                        Caută
                      </Button>
                    </div>
                  </div>
                  <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground select-none">
                    <input
                      type="checkbox"
                      checked={includeGraduatedUsers}
                      onChange={(e) => setIncludeGraduatedUsers(e.target.checked)}
                    />
                    Afișează utilizatorii absolviți (arhivați)
                  </label>
                </div>

                <div className="mt-4 bg-card border border-border rounded-lg overflow-hidden">
                  {usersLoading ? (
                    <div className="p-6 text-sm text-muted-foreground">Se încarcă...</div>
                  ) : users.length === 0 ? (
                    <div className="p-10 text-sm text-muted-foreground text-center">Nu există utilizatori.</div>
                  ) : (
                    <ul className="divide-y divide-border">
                      {pagedUsers.map((u) => (
                        <li
                          key={u.id}
                          className="p-4 flex items-start justify-between gap-4 cursor-pointer hover:bg-muted/40 transition-colors"
                          onClick={() => void openUserDetails(u.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') void openUserDetails(u.id);
                          }}
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="text-sm font-semibold text-foreground truncate">{u.full_name}</div>
                              {u.is_graduated ? (
                                <span className="text-[11px] px-2 py-0.5 rounded-md border border-warning/30 text-warning bg-warning/10">Absolvit</span>
                              ) : null}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">{u.email}</div>
                            <div className="text-xs text-muted-foreground mt-1">Rol: {u.role}</div>
                            {u.classroom_name ? (
                              <div className="text-xs text-muted-foreground mt-1">Clasă: {u.classroom_name}</div>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2">
                            {!isReadOnly ? (
                              <Button
                                variant={u.is_suspended ? 'outline' : 'destructive'}
                                iconName={u.is_suspended ? 'CheckCircle' : 'Ban'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void suspendToggle(u);
                                }}
                              >
                                {u.is_suspended ? 'Reactivează' : 'Suspendă'}
                              </Button>
                            ) : (
                              <span
                                className={`text-xs px-2 py-1 rounded-md border ${
                                  u.is_suspended ? 'border-error/30 text-error bg-error/10' : 'border-border text-muted-foreground bg-muted/30'
                                }`}
                              >
                                {u.is_suspended ? 'Suspendat' : 'Activ'}
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {!usersLoading && users.length > 0 ? (
                    <div className="border-t border-border p-3 flex items-center justify-between gap-3 flex-wrap text-xs text-muted-foreground">
                      <div>
                        Afișăm {Math.min(users.length, (usersPageSafe - 1) * PAGE_SIZE + 1)}–
                        {Math.min(users.length, usersPageSafe * PAGE_SIZE)} din {users.length}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" disabled={usersPageSafe <= 1} onClick={() => setUsersPage(usersPageSafe - 1)}>
                          Înapoi
                        </Button>
                        <span>
                          Pagina {usersPageSafe} / {usersTotalPages}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={usersPageSafe >= usersTotalPages}
                          onClick={() => setUsersPage(usersPageSafe + 1)}
                        >
                          Înainte
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}

            {tab === 'classes' ? (
              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div>
                    <div className="text-lg font-semibold text-foreground">Clase</div>
                    <div className="text-sm text-muted-foreground">Creeaza clase, seteaza diriginti si gestioneaza materii.</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      iconName="UserPlus"
                      onClick={() => {
                        setCreateTeacherOpen(true);
                        if (!teachers.length) void loadTeachers();
                      }}
                    >
                      Invita profesor
                    </Button>
                    <Button
                      variant="default"
                      iconName="Plus"
                      onClick={() => {
                        setCreateClassOpen(true);
                        if (!teachers.length) void loadTeachers();
                      }}
                      disabled={classesLoading}
                    >
                      Creeaza clasa
                    </Button>
                  </div>
                </div>
                {classesLoading ? (
                  <div className="text-sm text-muted-foreground">Se încarcă...</div>
                ) : classesError ? (
                  <div className="text-sm text-muted-foreground flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm">
                      <div className="font-medium text-foreground">Nu se pot Ã®ncÄƒrca clasele.</div>
                      <div className="mt-1">{classesError}</div>
                    </div>
                    <Button variant="outline" iconName="RefreshCw" onClick={() => void loadClasses()}>
                      ReÃ®ncarcÄƒ
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h2 className="text-sm font-semibold text-foreground mb-3">Clase (SchoolClass)</h2>
                      {(classes?.school_classes || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu există clase.</div>
                      ) : (
                        <ul className="space-y-2">
                          {classes?.school_classes.map((c) => (
                            <li key={c.id} className="p-3 rounded-md border border-border bg-background">
                              <div className="text-sm font-medium text-foreground">{c.name}</div>
                              <div className="mt-2 space-y-1">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-muted-foreground">Rating clasă</span>
                                  <span className="font-medium text-foreground">
                                    {typeof c.rating_percent === 'number' ? `${c.rating_percent}%` : '—'}
                                  </span>
                                </div>
                                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className={`h-full transition-all duration-300 ${typeof c.rating_percent === 'number' ? 'bg-primary' : 'bg-muted'}`}
                                    style={{ width: `${typeof c.rating_percent === 'number' ? c.rating_percent : 0}%` }}
                                    role="progressbar"
                                    aria-valuenow={typeof c.rating_percent === 'number' ? c.rating_percent : 0}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                  />
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                  {typeof c.avg_grade === 'number'
                                    ? `Media notelor: ${c.avg_grade.toFixed(2)} / 10 • ${c.grades_count ?? 0} note`
                                    : 'Fără note încă.'}
                                  {` • Abs. nemotivate: ${c.absences_unjustified ?? 0}/${c.absences_total ?? 0}`}
                                </div>
                              </div>
                              <div className="mt-2 flex items-center justify-end gap-2 flex-wrap">
                                <Button size="xs" variant="outline" iconName="BookOpen" onClick={() => void openSubjectsForClassroom(Number(c.id), String(c.name || ''))}>
                                  Materii
                                </Button>
                                {!isReadOnly ? (
                                  <Button
                                    size="xs"
                                    variant="outline"
                                    iconName="GraduationCap"
                                    loading={!!classGraduateLoading[Number(c.id)]}
                                    disabled={!!classGraduateLoading[Number(c.id)]}
                                    onClick={() => void graduateClassById(Number(c.id), String(c.name || ''))}
                                  >
                                    Absolvire
                                  </Button>
                                ) : null}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-foreground mb-3">Classrooms</h2>
                      {(classes?.classrooms || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu există classrooms.</div>
                      ) : (
                        <ul className="space-y-2">
                            {classes?.classrooms.map((c) => (
                              <li key={c.id} className="p-3 rounded-md border border-border bg-background">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="text-sm font-medium text-foreground">{c.name}</div>
                                  {!isReadOnly ? (
                                    <Button
                                      size="xs"
                                      variant="outline"
                                      iconName="GraduationCap"
                                      loading={!!classGraduateLoading[Number(c.id)]}
                                      disabled={!!classGraduateLoading[Number(c.id)]}
                                      onClick={() => void graduateClassById(Number(c.id), String(c.name || ''))}
                                    >
                                      Absolvire
                                    </Button>
                                  ) : null}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  Admini:{' '}
                                  {(c.admins || []).length > 0
                                    ? (c.admins || []).map((a) => a.full_name).join(', ')
                                  : c.admin_id != null
                                    ? `ID ${c.admin_id}`
                                    : '—'}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {tab === 'reports' ? (
              <div className="space-y-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div className="font-semibold text-foreground">Rapoarte</div>
                    <div className="w-full sm:w-auto flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadReports()} disabled={reportsLoading}>
                        Reîncarcă
                      </Button>
                      <Button
                        variant="outline"
                        iconName="Download"
                        onClick={() =>
                          downloadCsv(
                            `elevi_risc_${reportsDays}zile.csv`,
                            (reports?.items || []).map((x) => ({
                              id: x.id,
                              nume: x.full_name,
                              email: x.email,
                              clasa: x.classroom_name || '',
                              rating_percent: x.rating_percent ?? '',
                              media: x.avg_grade ?? '',
                              note: x.grades_count ?? 0,
                              absente_total: x.absences_total ?? 0,
                              absente_nemotivate: x.absences_unjustified ?? 0,
                            }))
                          )
                        }
                        disabled={reportsLoading || (reports?.items || []).length === 0}
                      >
                        Export CSV
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input
                      label="Perioadă (zile)"
                      type="number"
                      min={1}
                      max={365}
                      value={reportsDays}
                      onChange={(e) => setReportsDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                    />
                    <Input
                      label="Limită rezultate"
                      type="number"
                      min={1}
                      max={1000}
                      value={reportsLimit}
                      onChange={(e) => setReportsLimit(Math.max(1, Math.min(1000, Number(e.target.value) || 1)))}
                    />
                    <div className="flex items-end">
                      <Button variant="default" iconName="Search" onClick={() => void loadReports()} disabled={reportsLoading}>
                        Generează
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  {reportsLoading ? (
                    <div className="p-6 text-sm text-muted-foreground">Se încarcă...</div>
                  ) : (reports?.items || []).length === 0 ? (
                    <div className="p-10 text-sm text-muted-foreground text-center">Nu există date pentru raport.</div>
                  ) : (
                    <div className="overflow-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-muted-foreground border-b border-border">
                            <th className="text-left p-3">Elev</th>
                            <th className="text-left p-3">Clasă</th>
                            <th className="text-right p-3">Rating</th>
                            <th className="text-right p-3">Medie</th>
                            <th className="text-right p-3">Abs. nemotivate</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {pagedReportItems.map((x) => (
                            <tr key={x.id} className="hover:bg-muted/40">
                              <td className="p-3">
                                <div className="font-medium text-foreground">{x.full_name}</div>
                                <div className="text-xs text-muted-foreground">{x.email}</div>
                              </td>
                              <td className="p-3 text-foreground">{x.classroom_name || '—'}</td>
                              <td className="p-3 text-right font-medium text-foreground">
                                {typeof x.rating_percent === 'number' ? `${x.rating_percent}%` : '—'}
                              </td>
                              <td className="p-3 text-right text-foreground">
                                {typeof x.avg_grade === 'number' ? x.avg_grade.toFixed(2) : '—'}
                              </td>
                              <td className="p-3 text-right text-foreground">{x.absences_unjustified ?? 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div className="border-t border-border p-3 flex items-center justify-between gap-3 flex-wrap text-xs text-muted-foreground">
                        <div>
                          Afișăm {Math.min(reportItems.length, (reportsPageSafe - 1) * PAGE_SIZE + 1)}–{Math.min(reportItems.length, reportsPageSafe * PAGE_SIZE)} din{' '}
                          {reportItems.length}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" disabled={reportsPageSafe <= 1} onClick={() => setReportsPage(reportsPageSafe - 1)}>
                            Înapoi
                          </Button>
                          <span>
                            Pagina {reportsPageSafe} / {reportsTotalPages}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={reportsPageSafe >= reportsTotalPages}
                            onClick={() => setReportsPage(reportsPageSafe + 1)}
                          >
                            Înainte
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div className="font-semibold text-foreground">Top / Bottom clase după rating</div>
                    <div className="w-full sm:w-auto flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadClassRatings()} disabled={classRatingsLoading}>
                        Reîncarcă
                      </Button>
                      <Button
                        variant="outline"
                        iconName="Download"
                        disabled={classRatingsLoading || (classRatings?.items || []).length === 0}
                        onClick={() =>
                          downloadCsv(
                            'clase_rating.csv',
                            (classRatings?.items || []).map((x) => ({
                              id: x.id,
                              clasa: x.name,
                              rating_percent: x.rating_percent ?? '',
                              media: x.avg_grade ?? '',
                              note: x.grades_count ?? 0,
                              elevi: x.students_count ?? 0,
                              absente_total: x.absences_total ?? 0,
                              absente_nemotivate: x.absences_unjustified ?? 0,
                            }))
                          )
                        }
                      >
                        Export CSV
                      </Button>
                    </div>
                  </div>

                  {classRatingsLoading ? (
                    <div className="p-4 text-sm text-muted-foreground">Se încarcă...</div>
                  ) : (classRatings?.items || []).length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">Nu există date.</div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {(() => {
                        const items = classRatings?.items || [];
                        const byRatingAsc = [...items].sort((a, b) => (a.rating_percent == null ? 1 : 0) - (b.rating_percent == null ? 1 : 0) || (a.rating_percent ?? 0) - (b.rating_percent ?? 0));
                        const bottom = byRatingAsc.slice(0, 5);
                        const top = [...byRatingAsc].reverse().slice(0, 5);
                        const renderList = (title: string, rows: typeof items) => (
                          <div className="bg-background border border-border rounded-lg p-3">
                            <div className="text-sm font-semibold text-foreground mb-2">{title}</div>
                            <ul className="space-y-2">
                              {rows.map((c) => (
                                <li key={c.id} className="p-2 rounded-md border border-border">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-foreground">{c.name}</span>
                                    <span className="font-medium text-foreground">{typeof c.rating_percent === 'number' ? `${c.rating_percent}%` : '—'}</span>
                                  </div>
                                  <div className="mt-1 w-full h-2 bg-muted rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-primary"
                                      style={{ width: `${typeof c.rating_percent === 'number' ? c.rating_percent : 0}%` }}
                                      role="progressbar"
                                      aria-valuenow={typeof c.rating_percent === 'number' ? c.rating_percent : 0}
                                      aria-valuemin={0}
                                      aria-valuemax={100}
                                    />
                                  </div>
                                  <div className="mt-1 text-[11px] text-muted-foreground">
                                    {typeof c.avg_grade === 'number' ? `Medie: ${c.avg_grade.toFixed(2)} / 10` : 'Fără note'} • Abs. nemotivate: {c.absences_unjustified ?? 0}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                        return (
                          <>
                            {renderList('Top 5', top)}
                            {renderList('Bottom 5', bottom)}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>

                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                    <div className="font-semibold text-foreground">Absențe nemotivate pe clase</div>
                    <div className="w-full sm:w-auto flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                      <Button
                        variant={absDays === 7 ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAbsDays(7)}
                        disabled={absByClassLoading}
                      >
                        7 zile
                      </Button>
                      <Button
                        variant={absDays === 30 ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAbsDays(30)}
                        disabled={absByClassLoading}
                      >
                        30 zile
                      </Button>
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadAbsencesByClass()} disabled={absByClassLoading}>
                        Reîncarcă
                      </Button>
                      <Button
                        variant="outline"
                        iconName="Download"
                        disabled={absByClassLoading || (absByClass?.items || []).length === 0}
                        onClick={() =>
                          downloadCsv(
                            `absente_nemotivate_clase_${absDays}zile.csv`,
                            (absByClass?.items || []).map((x) => ({
                              id: x.id,
                              clasa: x.name,
                              elevi: x.students_count ?? 0,
                              absente_total: x.absences_total ?? 0,
                              absente_nemotivate: x.absences_unjustified ?? 0,
                            }))
                          )
                        }
                      >
                        Export CSV
                      </Button>
                    </div>
                  </div>

                  {absByClassLoading ? (
                    <div className="p-4 text-sm text-muted-foreground">Se încarcă...</div>
                  ) : (absByClass?.items || []).length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">Nu există date.</div>
                  ) : (
                    <div className="space-y-2">
                      {(() => {
                        const rows = absByClass?.items || [];
                        const max = Math.max(1, ...rows.map((r) => r.absences_unjustified || 0));
                        return rows.slice(0, 20).map((r) => (
                          <div key={r.id} className="bg-background border border-border rounded-lg p-3">
                            <div className="flex items-center justify-between text-sm">
                              <div className="text-foreground">{r.name}</div>
                              <div className="text-foreground font-medium">{r.absences_unjustified ?? 0}</div>
                            </div>
                            <div className="mt-2 w-full h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary" style={{ width: `${(((r.absences_unjustified ?? 0) / max) * 100).toFixed(2)}%` }} />
                            </div>
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              Total absențe: {r.absences_total ?? 0} • Elevi: {r.students_count ?? 0}
                            </div>
                          </div>
                        ));
                      })()}
                      {(absByClass?.items || []).length > 20 ? (
                        <div className="text-xs text-muted-foreground">Afișăm primele 20 de clase. Folosește Export CSV pentru toate.</div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {tab === 'audit' ? (
              <div className="space-y-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                      <div className="text-lg font-semibold text-foreground">Audit</div>
                      <div className="text-sm text-muted-foreground">Istoric modificari (note, absente) pentru scoala.</div>
                    </div>
                    <div className="w-full sm:w-auto flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                      <Button
                        variant="outline"
                        iconName="Download"
                        onClick={() => void exportAuditCsv()}
                        loading={auditExportCsvLoading}
                        disabled={auditLoading || auditExportCsvLoading}
                      >
                        Export CSV
                      </Button>
                      <Button
                        variant="outline"
                        iconName="Printer"
                        onClick={() => void exportAuditPdf()}
                        loading={auditExportPdfLoading}
                        disabled={auditLoading || auditExportPdfLoading}
                      >
                        Export PDF
                      </Button>
                      <Button variant="outline" iconName="RefreshCw" onClick={() => void loadAuditEvents()} disabled={auditLoading}>
                        Reincarca
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-6 gap-3">
                    <Select
                      label="Tip"
                      value={auditEntityType}
                      options={auditEntityOptions}
                      onChange={(v) => {
                        setAuditEntityType(String(v || ''));
                        setAuditOffset(0);
                      }}
                      searchable={false}
                      clearable={false}
                    />
                    <Input
                      label="Elev (cauta)"
                      placeholder="Nume / email..."
                      value={auditStudentQ}
                      onChange={(e) => setAuditStudentQ(e.target.value)}
                    />
                    <Select
                      label="Elev (select)"
                      value={auditStudentId ? String(auditStudentId) : ''}
                      options={auditStudentSelectOptions}
                      onChange={(v) => {
                        const s = String(v || '').trim();
                        setAuditStudentId(s ? Number(s) : null);
                        setAuditOffset(0);
                      }}
                      searchable
                      clearable
                      disabled={auditStudentLoading}
                    />
                    <Input
                      label="Cine (cauta)"
                      placeholder="Profesor / admin..."
                      value={auditActorQ}
                      onChange={(e) => setAuditActorQ(e.target.value)}
                    />
                    <Select
                      label="Cine (select)"
                      value={auditActorId ? String(auditActorId) : ''}
                      options={auditActorSelectOptions}
                      onChange={(v) => {
                        const s = String(v || '').trim();
                        setAuditActorId(s ? Number(s) : null);
                        setAuditOffset(0);
                      }}
                      searchable
                      clearable
                      disabled={auditActorLoading}
                    />
                    <Select
                      label="Clasa"
                      value={auditClassFilter}
                      options={[{ value: '', label: 'Toate clasele' }, ...(classes?.school_classes || []).map((c: any) => ({ value: String(c.id), label: c.name }))]}
                      onChange={(v) => {
                        setAuditClassFilter(String(v || ''));
                        setAuditOffset(0);
                      }}
                      searchable
                      clearable
                      disabled={!classes}
                    />
                  </div>

                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <Input label="Actiune (exact)" placeholder="grade.update" value={auditAction} onChange={(e) => setAuditAction(e.target.value)} />
                      <Input label="De la" type="date" value={auditFrom} onChange={(e) => setAuditFrom(e.target.value)} />
                      <Input label="Pana la" type="date" value={auditTo} onChange={(e) => setAuditTo(e.target.value)} />
                      <Select
                        label="Rezultate / pagina"
                        value={String(auditLimit || 10)}
                        options={auditPageSizeOptions}
                        onChange={(v) => {
                          const n = Math.max(1, Number(String(v || '10')) || 10);
                          setAuditLimit(n);
                          setAuditOffset(0);
                          void loadAuditEvents(0);
                        }}
                        searchable={false}
                        clearable={false}
                      />
                    </div>

                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
                      <div className="flex flex-wrap items-end gap-2">
                        <Button
                          variant="default"
                          iconName="Search"
                          size="sm"
                          onClick={() => {
                            setAuditOffset(0);
                            void loadAuditEvents(0);
                          }}
                          disabled={auditLoading}
                        >
                          Cauta
                        </Button>
                        <Button
                          variant="outline"
                          iconName="X"
                          size="sm"
                          onClick={() => {
                            setAuditEntityType('');
                            setAuditAction('');
                            setAuditFrom('');
                            setAuditTo('');
                            setAuditClassFilter('');
                            setAuditStudentQ('');
                            setAuditStudentId(null);
                            setAuditActorQ('');
                            setAuditActorId(null);
                            setAuditOffset(0);
                            void loadAuditEvents(0);
                          }}
                          disabled={auditLoading}
                        >
                          Reset
                        </Button>
                      </div>

                      <div className="flex items-center justify-end gap-2 w-full md:w-auto">
                        <Button
                          variant="outline"
                          iconName="ChevronLeft"
                          size="sm"
                          onClick={() => {
                            const nextOff = Math.max(0, (auditOffset || 0) - Math.max(1, auditLimit || 10));
                            setAuditOffset(nextOff);
                            void loadAuditEvents(nextOff);
                          }}
                          disabled={auditLoading || auditOffset <= 0}
                        >
                          Prev
                        </Button>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          Pagina {auditPage}
                        </div>
                        <Button
                          variant="outline"
                          iconName="ChevronRight"
                          size="sm"
                          onClick={() => {
                            const nextOff = (auditOffset || 0) + Math.max(1, auditLimit || 10);
                            setAuditOffset(nextOff);
                            void loadAuditEvents(nextOff);
                          }}
                          disabled={auditLoading || !auditHasMore}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  {auditLoading ? (
                    <div className="p-4 text-sm text-muted-foreground">Se incarca...</div>
                  ) : auditError ? (
                    <div className="p-4 text-sm text-error">{auditError}</div>
                  ) : (auditEvents || []).length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">Nu exista evenimente.</div>
                  ) : (
                    <div className="overflow-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-muted-foreground">
                            <th className="text-left py-2 px-3">Data</th>
                            <th className="text-left py-2 px-3">Tip</th>
                            <th className="text-left py-2 px-3">Elev</th>
                            <th className="text-left py-2 px-3">Actiune</th>
                            <th className="text-left py-2 px-3">Cine</th>
                            <th className="text-left py-2 px-3">IP</th>
                            <th className="text-left py-2 px-3">Detalii</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {(auditEvents || []).map((ev) => {
                            const typeLabel = ev.entity_type === 'grade' ? 'Nota' : ev.entity_type === 'absence' ? 'Absenta' : ev.entity_type || '—';
                            const oldV = ev.old_data?.value ?? ev.old_data?.qualifier ?? ev.old_data?.justified ?? '—';
                            const newV = ev.new_data?.value ?? ev.new_data?.qualifier ?? ev.new_data?.justified ?? '—';
                            const subj = ev.new_data?.subject_name || ev.old_data?.subject_name || ev.new_data?.subject_id || ev.old_data?.subject_id || '';
                            const change = `${oldV} → ${newV}`;
                            return (
                              <tr key={ev.id}>
                                <td className="py-2 px-3 whitespace-nowrap text-xs text-muted-foreground">
                                  {ev.created_at ? new Date(ev.created_at).toLocaleString('ro-RO') : '—'}
                                </td>
                                <td className="py-2 px-3 text-foreground whitespace-nowrap">{typeLabel}</td>
                                <td className="py-2 px-3 text-foreground">
                                  {ev.subject_name ? (
                                    <button
                                      className="text-left text-primary hover:underline"
                                      onClick={() => {
                                        if (ev.subject_user_id) void openUserDetails(ev.subject_user_id);
                                      }}
                                    >
                                      {ev.subject_name}
                                    </button>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{ev.action || '—'}</td>
                                <td className="py-2 px-3 text-muted-foreground">{ev.actor_name || '—'}</td>
                                <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{ev.actor_ip || '—'}</td>
                                <td className="py-2 px-3">
                                  <div className="text-foreground text-xs">#{ev.entity_id}</div>
                                  <div className="text-muted-foreground text-[11px] truncate max-w-[360px]">
                                    {(subj ? `Materie: ${subj} • ` : '') + `Schimbare: ${change}`}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {tab === 'settings' ? (
              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div>
                    <div className="text-lg font-semibold text-foreground">Setări</div>
                    <div className="text-sm text-muted-foreground">Reguli rating, praguri alerte și notificări.</div>
                  </div>
                  {!isReadOnly ? (
                    <Button variant="default" iconName="Save" onClick={() => void saveSettings()} disabled={settingsLoading || settingsSaving || !settings}>
                      Salvează
                    </Button>
                  ) : (
                    <div className="text-xs text-muted-foreground">Read-only</div>
                  )}
                </div>

                {settingsLoading ? (
                  <div className="p-6 text-sm text-muted-foreground">Se încarcă...</div>
                ) : !settings ? (
                  <div className="text-sm text-muted-foreground">Nu pot încărca setările.</div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input
                        label="Penalizare absențe (per elev)"
                        type="number"
                        min={0}
                        step="0.5"
                        value={settings.absence_penalty_per_student}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          setSettings((p) =>
                            p
                              ? {
                                  ...p,
                                  absence_penalty_per_student: Math.max(0, Number(e.target.value) || 0),
                                }
                              : p
                          )
                        }
                      />
                      <Input
                        label="Cap penalizare"
                        type="number"
                        min={0}
                        step="1"
                        value={settings.absence_penalty_cap}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          setSettings((p) =>
                            p
                              ? {
                                  ...p,
                                  absence_penalty_cap: Math.max(0, Number(e.target.value) || 0),
                                }
                              : p
                          )
                        }
                      />
                      <Input
                        label="Prag alertă absențe (7 zile)"
                        type="number"
                        min={0}
                        step="1"
                        value={settings.unjustified_absences_7d_threshold}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          setSettings((p) =>
                            p
                              ? {
                                  ...p,
                                  unjustified_absences_7d_threshold: Math.max(0, Number(e.target.value) || 0),
                                }
                              : p
                          )
                        }
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input
                        label="An scolar curent (default)"
                        value={settings.current_school_year || ''}
                        disabled={isReadOnly}
                        placeholder="ex: 2025-2026"
                        onChange={(e) => setSettings((p) => (p ? { ...p, current_school_year: String(e.target.value || '') } : p))}
                      />
                      <Select
                        label="Structura (default)"
                        value={settings.current_structure || ''}
                        options={[
                          { value: '', label: 'Nesetat' },
                          { value: 'semestru', label: 'Semestru' },
                          { value: 'trimestru', label: 'Trimestru' },
                          { value: 'module', label: 'Module' },
                        ]}
                        onChange={(v) => setSettings((p) => (p ? { ...p, current_structure: String(v || '') } : p))}
                        disabled={isReadOnly}
                      />
                      <Select
                        label="Nivel (default)"
                        value={settings.current_education_level || ''}
                        options={[
                          { value: '', label: 'Nesetat' },
                          { value: 'primar', label: 'Primar' },
                          { value: 'gimnazial', label: 'Gimnazial' },
                          { value: 'liceal', label: 'Liceal' },
                          { value: 'universitar', label: 'Universitar' },
                        ]}
                        onChange={(v) => setSettings((p) => (p ? { ...p, current_education_level: String(v || '') } : p))}
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <div className="bg-background border border-border rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <Input
                            type="checkbox"
                            checked={settings.auto_notifications_enabled}
                            disabled={isReadOnly}
                            onChange={(e) =>
                              setSettings((p) => (p ? { ...p, auto_notifications_enabled: e.target.checked } : p))
                            }
                          />
                          <div className="text-sm text-foreground">Auto-notificări</div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          Dacă e activ, sistemul va publica automat alertele ca notificări (la deschiderea panoului).
                        </div>
                      </div>

                      <Select
                        label="Frecvență"
                        value={settings.auto_notifications_frequency}
                        options={[
                          { value: 'daily', label: 'Zilnic' },
                          { value: 'weekly', label: 'Săptămânal' },
                        ]}
                        onChange={(v) => setSettings((p) => (p ? { ...p, auto_notifications_frequency: String(v) } : p))}
                        disabled={isReadOnly || !settings.auto_notifications_enabled}
                      />

                      <div className="text-xs text-muted-foreground">
                        Ultima publicare auto:{' '}
                        {settings.auto_notifications_last_sent_at
                          ? new Date(settings.auto_notifications_last_sent_at).toLocaleString('ro-RO')
                          : '—'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </main>
      </div>

      {createTeacherOpen ? (
        <div className="fixed inset-0 z-[1300] flex items-start sm:items-center justify-center overflow-y-auto bg-black/40 p-4" onMouseDown={() => setCreateTeacherOpen(false)}>
          <div className="w-full max-w-lg bg-card border border-border rounded-xl p-5" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-lg font-semibold text-foreground">Invita profesor</div>
                <div className="text-sm text-muted-foreground">Creeaza cont + trimite email cu parola temporara.</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setCreateTeacherOpen(false)} disabled={createTeacherLoading}>
                Inchide
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <Input
                label="Nume complet"
                value={createTeacherForm.full_name}
                onChange={(e) => setCreateTeacherForm((p) => ({ ...p, full_name: String(e.target.value || '') }))}
                disabled={createTeacherLoading}
              />
              <Input
                label="Email"
                value={createTeacherForm.email}
                onChange={(e) => setCreateTeacherForm((p) => ({ ...p, email: String(e.target.value || '') }))}
                disabled={createTeacherLoading}
              />
              <Input
                label="Telefon (optional)"
                value={createTeacherForm.phone}
                onChange={(e) => setCreateTeacherForm((p) => ({ ...p, phone: String(e.target.value || '') }))}
                disabled={createTeacherLoading}
              />
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateTeacherOpen(false)} disabled={createTeacherLoading}>
                Renunta
              </Button>
              <Button iconName="Send" onClick={() => void submitCreateTeacher()} loading={createTeacherLoading}>
                Trimite invitatie
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {createClassOpen ? (
        <div className="fixed inset-0 z-[1300] flex items-start sm:items-center justify-center overflow-y-auto bg-black/40 p-4" onMouseDown={() => setCreateClassOpen(false)}>
          <div className="w-full max-w-lg bg-card border border-border rounded-xl p-5" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-lg font-semibold text-foreground">Creeaza clasa</div>
                <div className="text-sm text-muted-foreground">Creeaza clasa in catalog + hub (sincronizat).</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setCreateClassOpen(false)} disabled={createClassLoading}>
                Inchide
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <Input
                label="Nume clasa"
                value={createClassName}
                onChange={(e) => setCreateClassName(String(e.target.value || ''))}
                disabled={createClassLoading}
                placeholder="ex: Clasa a VIII-a C"
              />
              <Select
                label="Diriginte / profesor admin (optional)"
                value={createClassAdminTeacherId}
                options={teacherOptions}
                onChange={(v) => setCreateClassAdminTeacherId(String(v || ''))}
                disabled={createClassLoading || teachersLoading}
              />
              {teachersLoading ? <div className="text-xs text-muted-foreground">Se incarca lista de profesori...</div> : null}
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateClassOpen(false)} disabled={createClassLoading}>
                Renunta
              </Button>
              <Button iconName="Plus" onClick={() => void submitCreateClass()} loading={createClassLoading}>
                Creeaza
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {subjectsOpen ? (
        <div className="fixed inset-0 z-[1300] flex items-start sm:items-center justify-center overflow-y-auto bg-black/40 p-4" onMouseDown={() => setSubjectsOpen(false)}>
          <div className="w-full max-w-3xl bg-card border border-border rounded-xl p-5" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-lg font-semibold text-foreground">Materii</div>
                <div className="text-sm text-muted-foreground">{subjectsClassroomName ? `Clasa: ${subjectsClassroomName}` : ''}</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setSubjectsOpen(false)}>
                Inchide
              </Button>
            </div>

            {subjectsError ? <div className="mb-3 text-sm text-error">{subjectsError}</div> : null}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-4">
              <Input label="Materie noua" value={newSubjectName} onChange={(e) => setNewSubjectName(String(e.target.value || ''))} disabled={newSubjectLoading} />
              <Select label="Profesor" value={newSubjectTeacherId} options={teacherOptions} onChange={(v) => setNewSubjectTeacherId(String(v || ''))} disabled={newSubjectLoading || teachersLoading} />
              <Button iconName="Plus" onClick={() => void createSubject()} loading={newSubjectLoading} disabled={!subjectsClassroomId}>
                Adauga
              </Button>
            </div>

            {subjectsLoading ? (
              <div className="text-sm text-muted-foreground">Se incarca...</div>
            ) : (subjects || []).length === 0 ? (
              <div className="text-sm text-muted-foreground">Nu exista materii.</div>
            ) : (
              <div className="overflow-auto border border-border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-muted-foreground">
                      <th className="text-left py-2 px-3">Materie</th>
                      <th className="text-left py-2 px-3">Profesor</th>
                      <th className="text-right py-2 px-3">Actiuni</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(subjects || []).map((s) => (
                      <tr key={s.id}>
                        <td className="py-2 px-3 text-foreground">{s.name}</td>
                        <td className="py-2 px-3">
                          <Select
                            value={String(s.professor_id)}
                            options={teacherOptions.filter((x) => x.value !== '')}
                            onChange={(v) => {
                              const pid = Number(v);
                              if (pid && pid !== s.professor_id) void updateSubjectTeacher(Number(s.id), pid);
                            }}
                          />
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Button size="xs" variant="outline" iconName="Trash2" onClick={() => void deleteSubject(Number(s.id))}>
                            Sterge
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {userModalOpen ? (
        <div className="fixed inset-0 z-[1200] flex items-start sm:items-center justify-center overflow-y-auto bg-black/40 p-4" onMouseDown={closeUserModal}>
          <div
            className="w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto bg-card border border-border rounded-xl p-5"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="text-lg font-semibold text-foreground truncate">
                  {userDetailsLoading ? 'Se încarcă...' : selectedUserDetails?.full_name || '—'}
                </div>
                <div className="text-sm text-muted-foreground">
                  {selectedUserDetails?.email || ''}{selectedUserDetails?.role ? ` • ${roleLabel(selectedUserDetails.role)}` : ''}
                </div>
                {selectedUserDetails?.is_graduated ? (
                  <div className="mt-2 inline-flex items-center gap-2 text-xs px-2 py-1 rounded-md border border-warning/30 text-warning bg-warning/10">
                    <Icon name="Archive" size={14} />
                    Arhivat (absolvire)
                    {selectedUserDetails?.graduated_at ? ` • ${new Date(selectedUserDetails.graduated_at).toLocaleString('ro-RO')}` : ''}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={() => void exportSelectedUserJson()}
                  loading={userExportLoading}
                  disabled={!selectedUserDetails || userExportLoading || userDetailsLoading}
                >
                  Export
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName={userEditMode ? 'X' : 'Edit'}
                  onClick={() => {
                    if (!selectedUserDetails) return;
                    const next = !userEditMode;
                    setUserEditMode(next);
                    if (next) {
                      setUserEditForm({
                        full_name: String(selectedUserDetails.full_name || ''),
                        email: String(selectedUserDetails.email || ''),
                        phone: String((selectedUserDetails as any).phone || ''),
                        address: String((selectedUserDetails as any).address || ''),
                      });
                    }
                  }}
                  disabled={!selectedUserDetails || userDetailsLoading}
                >
                  {userEditMode ? 'Renunță' : 'Editează'}
                </Button>
                {!isReadOnly && selectedUserDetails && String(selectedUserDetails.role || '').toLowerCase() === 'elev' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="GraduationCap"
                    onClick={() => void graduateSelectedUser()}
                    loading={userGraduateLoading}
                    disabled={!selectedUserDetails || userGraduateLoading || userDetailsLoading || !!selectedUserDetails.is_graduated}
                  >
                    Absolvire
                  </Button>
                ) : null}
                <Button
                  variant="destructive"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => void deleteSelectedUser()}
                  loading={userDeleteLoading}
                  disabled={
                    !selectedUserDetails ||
                    userDeleteLoading ||
                    userDetailsLoading ||
                    String(selectedUserDetails.role || '').toLowerCase() === 'admin' ||
                    String(selectedUserDetails.role || '').toLowerCase() === 'profesor' ||
                    Number(selectedUserDetails.id) === Number(me?.id)
                  }
                >
                  Șterge
                </Button>
                <button
                  className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
                  onClick={closeUserModal}
                  aria-label="Închide"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
            </div>

            {userDetailsLoading ? (
              <div className="p-6 text-sm text-muted-foreground">Se încarcă detaliile...</div>
            ) : selectedUserDetails ? (
              <div className="space-y-4">
                {userEditMode ? (
                  <div className="bg-background border border-border rounded-lg p-3">
                    <div className="text-sm font-semibold text-foreground mb-3">Editare cont</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input
                        label="Nume"
                        value={userEditForm.full_name}
                        onChange={(e) => setUserEditForm((p) => ({ ...p, full_name: e.target.value }))}
                      />
                      <Input
                        label="Email"
                        type="email"
                        value={userEditForm.email}
                        onChange={(e) => setUserEditForm((p) => ({ ...p, email: e.target.value }))}
                      />
                      <Input
                        label="Telefon"
                        value={userEditForm.phone}
                        onChange={(e) => setUserEditForm((p) => ({ ...p, phone: e.target.value }))}
                      />
                      <Input
                        label="Adresă"
                        value={userEditForm.address}
                        onChange={(e) => setUserEditForm((p) => ({ ...p, address: e.target.value }))}
                      />
                    </div>
                    <div className="mt-3 flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUserEditMode(false);
                          setUserEditForm({
                            full_name: String(selectedUserDetails.full_name || ''),
                            email: String(selectedUserDetails.email || ''),
                            phone: String((selectedUserDetails as any).phone || ''),
                            address: String((selectedUserDetails as any).address || ''),
                          });
                        }}
                        disabled={userEditSaving}
                      >
                        Renunță
                      </Button>
                      <Button size="sm" iconName="Save" onClick={() => void saveUserEdits()} loading={userEditSaving}>
                        Salvează
                      </Button>
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">
                      Notă: ștergerea este blocată pentru conturi de admin/profesor (folosește Suspendare).
                    </div>
                  </div>
                ) : null}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-background border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">Telefon</div>
                    <div className="text-sm text-foreground mt-1">{selectedUserDetails.phone || '—'}</div>
                  </div>
                  <div className="bg-background border border-border rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">Clasă</div>
                    <div className="text-sm text-foreground mt-1">{selectedUserDetails.classroom?.name || '—'}</div>
                  </div>
                </div>

                {(selectedUserDetails.role || '').toLowerCase() === 'profesor' ? (
                  <div className="bg-background border border-border rounded-lg p-3">
                    <div className="text-sm font-semibold text-foreground mb-2">Materii predate</div>
                    {(selectedUserDetails.subjects || []).length === 0 ? (
                      <div className="text-sm text-muted-foreground">Nu are materii asignate.</div>
                    ) : (
                      <ul className="space-y-2">
                        {(selectedUserDetails.subjects || []).map((s) => (
                          <li key={s.id} className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-foreground">{s.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {s.classroom_name ? `Clasa: ${s.classroom_name}` : 'Clasă: —'}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}

                {(selectedUserDetails.role || '').toLowerCase() === 'parinte' ? (
                  <div className="bg-background border border-border rounded-lg p-3">
                    <div className="text-sm font-semibold text-foreground mb-2">Copii</div>
                    {(selectedUserDetails.children || []).length === 0 ? (
                      <div className="text-sm text-muted-foreground">Nu are copii asociați.</div>
                    ) : (
                      <ul className="space-y-2">
                        {(selectedUserDetails.children || []).map((c) => (
                          <li key={c.id} className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm text-foreground">{c.full_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {c.classroom_name ? `Clasa: ${c.classroom_name}` : 'Clasă: —'} • {c.email}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}

                {(selectedUserDetails.role || '').toLowerCase() === 'elev' ? (
                  <>
                    <div className="bg-background border border-border rounded-lg p-3">
                      <div className="text-sm font-semibold text-foreground mb-2">Părinți</div>
                      {(selectedUserDetails.parents || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu are părinți asociați.</div>
                      ) : (
                        <ul className="space-y-2">
                          {(selectedUserDetails.parents || []).map((p, idx) => (
                            <li key={`${p.email || 'p'}-${idx}`} className="text-sm text-foreground">
                              {(p.full_name || '—') + (p.email ? ` • ${p.email}` : '') + (p.phone ? ` • ${p.phone}` : '')}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="bg-background border border-border rounded-lg p-3">
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                        <div className="text-sm font-semibold text-foreground">Catalog</div>
                        {selectedUserDetails.classroom?.id ? (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="BookOpen"
                            onClick={() => navigate(`/class-management-hub?classId=${selectedUserDetails.classroom?.id}&tab=catalog`)}
                          >
                            Deschide catalogul clasei
                          </Button>
                        ) : null}
                      </div>

                      <div className="text-xs text-muted-foreground mb-3">
                        Absențe: {selectedUserDetails.catalog?.absences_unjustified ?? 0} nemotivate / {selectedUserDetails.catalog?.absences_total ?? 0} total
                      </div>

                      {(selectedUserDetails.catalog?.subjects || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu există note încă.</div>
                      ) : (
                        <div className="overflow-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-muted-foreground">
                                <th className="text-left py-2">Materie</th>
                                <th className="text-right py-2">Medie</th>
                                <th className="text-right py-2">Note</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {(selectedUserDetails.catalog?.subjects || []).map((s, idx) => (
                                <tr key={`${s.subject_id ?? 'x'}-${idx}`}>
                                  <td className="py-2">{s.subject_name || '—'}</td>
                                  <td className="py-2 text-right">{typeof s.avg_grade === 'number' ? s.avg_grade.toFixed(2) : '—'}</td>
                                  <td className="py-2 text-right">{s.grades_count ?? 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    <div className="bg-background border border-border rounded-lg p-3">
                      <div className="text-sm font-semibold text-foreground mb-2">Audit note (ultimele 200)</div>
                      {studentGradeAuditLoading ? (
                        <div className="text-sm text-muted-foreground">Se incarca...</div>
                      ) : studentGradeAuditError ? (
                        <div className="text-sm text-error">{studentGradeAuditError}</div>
                      ) : (studentGradeAudit || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu exista evenimente de audit.</div>
                      ) : (
                        <div className="overflow-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-muted-foreground">
                                <th className="text-left py-2">Data</th>
                                <th className="text-left py-2">Materie</th>
                                <th className="text-left py-2">Actiune</th>
                                <th className="text-left py-2">Cine</th>
                                <th className="text-left py-2">IP</th>
                                <th className="text-left py-2">Schimbare</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {(studentGradeAudit || []).map((ev) => {
                                const g = selectedStudentGradesById.get(ev.grade_id);
                                const subjLabel = g
                                  ? g.is_conduct
                                    ? 'Purtare'
                                    : g.subject_name || `Nota #${ev.grade_id}`
                                  : ev?.old_data?.is_conduct
                                  ? 'Purtare'
                                  : `Nota #${ev.grade_id}`;

                                const fmtVal = (d: any) =>
                                  typeof d?.value === 'number' ? String(d.value) : d?.qualifier ? String(d.qualifier) : '—';
                                const oldV = fmtVal(ev?.old_data);
                                const newV = fmtVal(ev?.new_data);

                                const action = String(ev.action || '');
                                const actionLabel =
                                  action === 'grade.create'
                                    ? 'creare'
                                    : action === 'grade.update'
                                    ? 'modificare'
                                    : action === 'grade.delete'
                                    ? 'stergere'
                                    : action;

                                return (
                                  <tr key={ev.id}>
                                    <td className="py-2 whitespace-nowrap text-xs text-muted-foreground">
                                      {ev.created_at ? new Date(ev.created_at).toLocaleString('ro-RO') : '—'}
                                    </td>
                                    <td className="py-2 text-foreground">{subjLabel}</td>
                                    <td className="py-2 text-muted-foreground">{actionLabel || '—'}</td>
                                    <td className="py-2 text-muted-foreground">{ev.actor_name || '—'}</td>
                                    <td className="py-2 text-muted-foreground">{ev.actor_ip || '—'}</td>
                                    <td className="py-2 text-foreground">
                                      <span className="text-muted-foreground">{oldV}</span> {'→'} <span>{newV}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    <div className="bg-background border border-border rounded-lg p-3">
                      <div className="text-sm font-semibold text-foreground mb-2">
                        Note (ultimele {selectedUserDetails.catalog_full?.limit ?? 0})
                      </div>
                      {(selectedUserDetails.catalog_full?.grades || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu există note.</div>
                      ) : (
                        <div className="overflow-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-muted-foreground">
                                <th className="text-left py-2">Data</th>
                                <th className="text-left py-2">Materie</th>
                                <th className="text-right py-2">Nota</th>
                                <th className="text-left py-2">Profesor</th>
                                <th className="text-left py-2">Comentariu</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {(selectedUserDetails.catalog_full?.grades || []).map((g) => (
                                <tr key={g.id}>
                                  <td className="py-2 whitespace-nowrap text-xs text-muted-foreground">
                                    {g.date ? new Date(g.date).toLocaleDateString('ro-RO') : g.created_at ? new Date(g.created_at).toLocaleDateString('ro-RO') : '—'}
                                  </td>
                                  <td className="py-2">
                                    <div className="text-foreground">{g.is_conduct ? 'Purtare' : g.subject_name || '—'}</div>
                                    {(g.is_thesis || g.is_final || g.is_homework) && (
                                      <div className="text-[11px] text-muted-foreground">
                                        {[
                                          g.is_thesis ? 'teză' : null,
                                          g.is_final ? 'final' : null,
                                          g.is_homework ? 'temă' : null,
                                        ]
                                          .filter(Boolean)
                                          .join(' • ')}
                                      </div>
                                    )}
                                  </td>
                                  <td className="py-2 text-right font-medium text-foreground">
                                    {typeof g.value === 'number' ? g.value : g.qualifier ? g.qualifier : '—'}
                                  </td>
                                  <td className="py-2 text-muted-foreground">{g.teacher_name || '—'}</td>
                                  <td className="py-2 text-muted-foreground">{g.comment || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    <div className="bg-background border border-border rounded-lg p-3">
                      <div className="text-sm font-semibold text-foreground mb-2">
                        Absențe (ultimele {selectedUserDetails.catalog_full?.limit ?? 0})
                      </div>
                      {(selectedUserDetails.catalog_full?.absences || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu există absențe.</div>
                      ) : (
                        <div className="overflow-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-xs text-muted-foreground">
                                <th className="text-left py-2">Data</th>
                                <th className="text-left py-2">Materie</th>
                                <th className="text-left py-2">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {(selectedUserDetails.catalog_full?.absences || []).map((a) => (
                                <tr key={a.id}>
                                  <td className="py-2 whitespace-nowrap text-xs text-muted-foreground">
                                    {a.date ? new Date(a.date).toLocaleDateString('ro-RO') : a.created_at ? new Date(a.created_at).toLocaleDateString('ro-RO') : '—'}
                                  </td>
                                  <td className="py-2 text-foreground">{a.subject_name || '—'}</td>
                                  <td className="py-2">
                                    <span className={a.justified ? 'text-success' : 'text-error'}>
                                      {a.justified ? 'motivat' : 'nemotivat'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
